import { create } from 'zustand';
import type {
  ActiveEnemy,
  CombatState,
  DropTier,
  EnemyDef,
  EquipmentInstance,
  Player,
  Rarity,
  VaultDef,
} from '@/domain/types';
import { getEnemy, BOSS_CD_MULTIPLIER } from '@/domain/enemies';
import { getVault } from '@/domain/vaults';
import { getBoss } from '@/domain/bosses';
import {
  calcEnemyDamage, calcPlayerDamage, calcSpecialDamage,
  calcShieldBashDamage, getEquippedWeaponClass, getWeaponProfile,
} from '@/domain/combat';
import type { PlayerCombatStats } from '@/domain/combat';
import { itemsInSet, getItem } from '@/domain/items';
import { currentStreakMult, pushStreakEntry, randomId } from '@/domain/player';
import { getEquippedStats, minEquippedRarity, rarityRank, getEquippedBonusMult, countGlitchPerkStacks, calcEquippedItemPower } from '@/domain/equipment';
import type { BonusStat, BonusStatKey } from '@/domain/types';
import { boostMultiplier } from '@/domain/boosts';
import { hasSkill, activeSkillValue } from '@/domain/skills';
import { totalAugmentValue } from '@/domain/augments';
import { resolvePassiveById, totalAspectValue } from '@/domain/aspects';
import { hasSetBonus, getSetBonusValue, hasLegendaryEffect } from '@/domain/itemSets';
import { useGroupStore, groupLootBonus } from '@/state/groupStore';
import { useGameStore } from '@/state/gameStore';
import { logRunEnd } from '@/state/telemetry';
import { nodeLevel } from '@/domain/skillWeb';
// Siphon-Tap removed in P2.A.5 sunset (was the Heat-gated in-combat tap).

// Special-bar fills at a constant 20 charge / second — divided by the equipped
// weapon's hits-per-second so all weapon classes need ~5 s of stationary combat
// to auto-cast a special. Without this scaling, Fast (4 hits/s) would fill in
// 1.25 s while Heavy (0.8 hits/s) needed 6.25 s.
const SPECIAL_CHARGE_RATE_PER_SEC = 20;
const SPECIAL_MAX = 100;
const GAMBLER_STEP = 0.002;
const KINETIC_MAX_STACKS = 50;
const MAX_EXTRA_WAVES = 4;
const HP_SCALE_PER_LEVEL = 0.04;  // +4% HP per level above vault minimum
const HP_SCALE_CAP = 3.0;         // max 3× enemy HP

/**
 * DEBUG: when true, every world-boss kill guarantees an exclusive drop and
 * the picked item rotates round-robin through the boss's exclusiveItemIds —
 * so the player can quickly collect each unique reward (Zeus-Blitz, etc.)
 * to test their visuals. Set back to `false` for normal RNG balancing.
 */
const DEBUG_FORCE_BOSS_EXCLUSIVE = false;
const debugExclusiveCounter: Record<string, number> = {};

const NEXT_DROP_TIER: Partial<Record<DropTier, DropTier>> = {
  common: 'uncommon', uncommon: 'rare', rare: 'epic', epic: 'legendary',
};

// ── Endless-Mode tuning ───────────────────────────────────────
/** Sentinel vaultId for the Endless Fight arena. No matching VaultDef — runs
 *  use store flags (`isEndless`) instead of vault.dropTier / vault.waves. */
export const ENDLESS_VAULT_ID = 'endless_arena';
/** Hard sentinel for `totalWaves` in endless runs — high enough that the
 *  existing `waveIndex < totalWaves` short-circuits never trigger early-
 *  victory. Reaching this would mean wave 9999 was cleared; not happening. */
const ENDLESS_SENTINEL_WAVES = 9999;

/** Drop tier escalation per endless wave reached. P2.C-tuned curve:
 *  Legendary is now gated to W30 (was W18) so the endless arena keeps a
 *  longer carrot — players have a real reason to push past W18 instead of
 *  farming the legendary-floor wave. W18-W29 still drops epics but with the
 *  pity + ascension-fragment system topping up the legendary-chase feel. */
function endlessDropTierForWave(waveIndex: number): DropTier {
  if (waveIndex >= 30) return 'legendary';
  if (waveIndex >= 12) return 'epic';
  if (waveIndex >= 7) return 'rare';
  if (waveIndex >= 3) return 'uncommon';
  return 'common';
}

/** Helper read used by every rollTierLoot call site: endless mode overrides
 *  the static vault.dropTier with a wave-driven one; everything else keeps
 *  the existing fallback. */
function effectiveDropTier(s: DungeonStoreState, vault: VaultDef | null | undefined): DropTier {
  if (s.isEndless) return endlessDropTierForWave(s.waveIndex);
  return vault?.dropTier ?? 'epic';
}

// Curated endless mob pool — 4 mid-difficulty mobs chosen for SWARM balance
// (not the world-boss tier-5 mobs that broke wave 17+). Visual variety stays
// reasonable without dropping a Tier-5 nexus_apex as a regular mob.
const ENDLESS_TRASH_POOL = ['swarmling', 'glitch_drone', 'corrupt_relay', 'nano_swarm'] as const;
// Hand-picked boss for each "every 10 waves" boss encounter. Cycles through
// the list, so W10=nexus_apex, W20=omega_herald, W30=nexus_apex again, etc.
// These are the actual Tier-5 designs that make a proper boss moment.
const ENDLESS_BOSSES = ['nexus_apex', 'omega_herald'] as const;
// Union of both — used by CombatViewStore3D to preload all GLTFs at endless
// mount, so no tier-jump compile-stall ever happens.
export const ENDLESS_ENEMY_POOL_IDS = [
  ...ENDLESS_TRASH_POOL,
  ...ENDLESS_BOSSES,
] as const;

/** Pick a wave roster for the given endless waveIndex. Vampire-Survivors-
 *  style: fixed trash pool, linear scaling, NO tier escalation. Difficulty
 *  comes from multipliers on HP+ATK+count, not from swapping in stronger
 *  mob types. Every 10 waves: one curated boss (cycles through ENDLESS_BOSSES).
 *
 *  The previous tier-pool approach added world-boss-class mobs (nexus_apex,
 *  omega_herald) to the regular pool at wave 16+, which spawned 8 bosses
 *  simultaneously — broken by design. This curve replaces that with one
 *  big-boss moment every 10 waves and otherwise sticks to the trash pool. */
function buildEndlessWave(waveIndex: number, hpMult: number): ActiveEnemy[] {
  const wIdx = Math.max(0, waveIndex);

  // Boss waves every 10 (W10, W20, W30, ...): single big enemy from the
  // curated boss list, +3× HP on top of the run hpMult.
  if (wIdx > 0 && wIdx % 10 === 0) {
    const bossId = ENDLESS_BOSSES[Math.floor((wIdx / 10 - 1)) % ENDLESS_BOSSES.length];
    const def = getEnemy(bossId);
    if (!def) return [];
    const scaled = Math.round(def.maxHp * hpMult * 3);
    return [{ instanceId: randomId('en'), enemyId: bossId, hp: scaled, maxHp: scaled }];
  }

  // Normal wave: 4-10 mobs from the curated trash pool. Distribution via a
  // wave-deterministic stride so the same wave always has the same roster
  // (good for replay-spotting on the leaderboard).
  const count = Math.min(10, 4 + Math.floor(wIdx / 2));
  const out: ActiveEnemy[] = [];
  for (let i = 0; i < count; i++) {
    const id = ENDLESS_TRASH_POOL[(wIdx * 7 + i * 3) % ENDLESS_TRASH_POOL.length];
    const def = getEnemy(id);
    if (!def) continue;
    const hp = Math.round(def.maxHp * hpMult);
    out.push({ instanceId: randomId('en'), enemyId: id, hp, maxHp: hp });
  }
  return out;
}

/** HP curve: +25% per wave. Wave 10 = 3.5×, Wave 20 = 6.0×, Wave 30 = 8.5×.
 *  No cap — at very high waves the HP just becomes the dominant difficulty
 *  lever and player gear-progression has to keep pace. */
function calcEndlessHpMult(waveIndex: number): number {
  return 1.0 + waveIndex * 0.25;
}

/** Attack curve: P2.C-tuned +20% per wave (was +10%). The flatter curve
 *  made W30+ trivial vs. enemy HP scaling — doubling the slope keeps the
 *  push-mode feeling like an actual difficulty climb. Wave 10 = 3.0×,
 *  W20 = 5.0×, W30 = 7.0×. Applied to every enemy.attack read via the
 *  store's `scaledAtkMult` field. */
function calcEndlessAtkMult(waveIndex: number): number {
  return 1.0 + waveIndex * 0.20;
}

// ── Item stat rolling ─────────────────────────────────────────

/** Roll base stats for a dropped item: each non-zero stat rolls 0 to 2× its design value. */
function rollBaseStats(def: { stats?: { attack: number; defense: number; health: number } }): { attack: number; defense: number; health: number } | undefined {
  if (!def.stats) return undefined;
  const roll = (base: number): number =>
    base <= 0 ? 0 : Math.floor(Math.random() * (base * 2 + 1));
  return { attack: roll(def.stats.attack), defense: roll(def.stats.defense), health: roll(def.stats.health) };
}

const BONUS_COUNTS: Record<string, [number, number]> = {
  common: [0, 0], uncommon: [0, 1], rare: [1, 2], epic: [2, 3], legendary: [3, 4],
};
const FLAT_RANGES: Record<string, [number, number]> = {
  common: [0, 0], uncommon: [1, 5], rare: [2, 15], epic: [5, 30], legendary: [10, 60],
};
const MULT_RANGES: Record<string, [number, number]> = {
  common: [0, 0], uncommon: [0, 0], rare: [2, 8], epic: [5, 15], legendary: [10, 25],
};
const FLAT_BONUS_KEYS: BonusStatKey[] = ['attack', 'defense', 'health'];
const MULT_BONUS_KEYS: BonusStatKey[] = ['loot_chance', 'xp_gain'];

export function rollBonusStats(rarity: string): BonusStat[] {
  const [minCount, maxCount] = BONUS_COUNTS[rarity] ?? [0, 0];
  const count = minCount + Math.floor(Math.random() * (maxCount - minCount + 1));
  if (count === 0) return [];

  const canMult = rarity === 'rare' || rarity === 'epic' || rarity === 'legendary';
  const usedKeys = new Set<BonusStatKey>();
  const result: BonusStat[] = [];

  for (let i = 0; i < count; i++) {
    const useMultiplier = canMult && Math.random() < 0.3;
    const pool = useMultiplier ? MULT_BONUS_KEYS : FLAT_BONUS_KEYS;
    const unused = pool.filter((k) => !usedKeys.has(k));
    if (unused.length === 0) continue;

    const stat = unused[Math.floor(Math.random() * unused.length)];
    usedKeys.add(stat);

    const [vMin, vMax] = useMultiplier ? MULT_RANGES[rarity] : FLAT_RANGES[rarity];
    const rawValue = vMin + Math.floor(Math.random() * (vMax - vMin + 1));
    const value = useMultiplier ? rawValue / 100 : rawValue;
    result.push({ stat, value, isMultiplier: useMultiplier });
  }
  return result;
}

// ── Aspect-telemetry helper ───────────────────────────────────
// Called from the 3 run-end paths: claimLoot (victory), flee, defeat.
// Account-mode runs only — local characters short-circuit inside
// `logRunEnd` (no userId binding).
//
// Idempotency: clears `runStartAt` after emitting so a follow-up call
// (e.g. flee → endless routes through victory → claimLoot) doesn't
// double-log the same run. The next startRun/startEndless/startBoss sets
// a fresh `runStartAt`.
function emitRunTelemetry(
  eventType: 'victory' | 'flee' | 'defeat',
  state: { vaultId: string | null; waveIndex: number; runStartAt?: number },
): void {
  const player = useGameStore.getState().player;
  if (!player || !state.vaultId || !state.runStartAt) return;
  void logRunEnd({
    characterId: player.id,
    userId: player.userId ?? '',
    eventType,
    vaultId: state.vaultId,
    waveReached: state.waveIndex,
    primaryAspect: player.aspects?.primary ?? null,
    secondaryAspect: player.aspects?.secondary ?? null,
    aspectLevels: player.aspects?.levels ?? {},
    activeSkillId: player.activeSkillId ?? null,
    itemPower: calcEquippedItemPower(player.equipment),
    runDurationMs: Date.now() - state.runStartAt,
  });
  useDungeonStore.setState({ runStartAt: 0 });
}

// ── Base combat state ─────────────────────────────────────────

const IDLE_STATE: CombatState = {
  phase: 'idle', vaultId: null,
  waveIndex: 0, enemyIndex: 0,
  playerHp: 0, playerMaxHp: 0,
  enemyHp: 0, enemyMaxHp: 0,
  currentEnemy: null, specialCharge: 0,
  loot: [], equipmentLoot: [],
  earnedXp: 0, earnedCorruptedData: 0,
  lastHpRegenAt: undefined,
};

// ── Drop tables ───────────────────────────────────────────────

type DropKey =
  | 'scrap' | 'energy_cell' | 'nano_chip' | 'data_track' | 'hyper_processor_5'
  // Hollow-Knight set is the sole equipment-drop pool in regular dungeons.
  // Legendary weapons drop via the world-boss `exclusiveItemIds` path, not from here.
  | 'equip_set';

// P2.C Re-tuning (round 5, D2-LoD calibrated by user playtest):
//
// User reference: in D2 LoD with 500 MF, they did 30× 5-minute Baal runs to
// get 1 legendary (some runs lucky, average ~15-20 runs per legendary).
// At ~50 kills per Baal run, that's:
//     1 legendary / (30 runs × 50 kills) = 0.067% legendary per kill
//
// My previous numbers + the boss fallback (25% per boss kill) were producing
// ~25-30% per run = ~1 per 3-4 runs. Two structural fixes this round:
//
//   1. Base rates dropped ~3× more (legendary 2% → 0.5%)
//   2. Boss fallback now produces ONLY rare/epic, never legendary —
//      legendaries must come from the explicit equip_set roll, not from
//      "ensure boss drops something" safety net (see pickFromEquipPool).
//
// New per-vault math (legendary tier, max ~1.5× multiplier, 25% legendary
// pool share):
//   per-kill legendary = 0.5% × 1.5 × 0.25 = 0.19% per kill
//   30-kill vault     → ~5.5% chance for 1 legendary → 1 per ~18 runs
//   50-kill endless   → ~9% chance → 1 per ~11 runs
//   D2 reference: 1 per ~15-20 runs. Hits the band.
//
// Per-run cap still in place (1 vault / 3 endless) as the ceiling against
// any multi-roll outlier.
const DROP_TABLES: Record<DropTier, Array<[DropKey, number]>> = {
  common: [
    ['scrap', 0.6], ['energy_cell', 0.25], ['nano_chip', 0.05],
    ['equip_set', 0.0005],
  ],
  uncommon: [
    ['scrap', 0.4], ['energy_cell', 0.2], ['nano_chip', 0.25], ['data_track', 0.05],
    ['equip_set', 0.0015],
  ],
  rare: [
    ['scrap', 0.2], ['energy_cell', 0.15], ['nano_chip', 0.3], ['data_track', 0.2],
    ['hyper_processor_5', 0.04],
    ['equip_set', 0.0025],
  ],
  epic: [
    ['scrap', 0.08], ['energy_cell', 0.08], ['nano_chip', 0.25], ['data_track', 0.3],
    ['hyper_processor_5', 0.12],
    ['equip_set', 0.004],
  ],
  legendary: [
    ['nano_chip', 0.1], ['data_track', 0.3], ['hyper_processor_5', 0.28],
    ['equip_set', 0.005],
  ],
};

/** Hard ceiling on the combined loot-multiplier stack (boost × augments ×
 *  equipment-perks × runLootMult). Anything above this cap is wasted —
 *  prevents 8×-HK + Lucky-Scan + Hunter5 stacks from inflating drop chances
 *  4× beyond the design intent. */
const MAX_LOOT_MULTIPLIER = 1.5;

/** Per-RUN legendary cap. Once the count reaches this, further equip_set
 *  rolls in the same run skip the legendary slice of pickFromEquipPool() and
 *  emit only rare/epic HK pieces. Vaults: 1 legendary per run. Endless:
 *  3 (longer runs justify a slightly higher ceiling). */
const LEGENDARY_RUN_CAP_VAULT = 1;
const LEGENDARY_RUN_CAP_ENDLESS = 3;

/**
 * Level-overshoot drop bonus: being above vault minimum level improves the
 * equipment-drop chance. P2.C-tuned: now PROPORTIONAL to the tier's base rate,
 * not a flat additive. The previous flat +20% absolute completely dominated
 * a 0.5% common-tier base (made every kill a near-guaranteed drop for any
 * high-level player farming early vaults). Now it tops out at +20% RELATIVE
 * — at 5% base, that's +1% absolute. Sane.
 */
function getLevelDropBonus(key: DropKey, overshoot: number, baseChance: number): number {
  if (key !== 'equip_set' || overshoot < 10) return 0;
  const tier = Math.min(4, Math.floor(overshoot / 10));
  // +5/+10/+15/+20 % RELATIVE to base — scales naturally across all tiers.
  return baseChance * tier * 0.05;
}

interface DropRollResult {
  resources: Array<{ itemId: string; quantity: number }>;
  equipment: EquipmentInstance[];
}

function rollUpgradeOnDrop(rarity: Rarity): number {
  switch (rarity) {
    case 'uncommon':  return Math.floor(Math.random() * 2);        // 0-1
    case 'rare':      return 1 + Math.floor(Math.random() * 3);    // 1-3
    case 'epic':      return 3 + Math.floor(Math.random() * 4);    // 3-6
    case 'legendary': return 5 + Math.floor(Math.random() * 6);    // 5-10
    case 'mythic':    return 5 + Math.floor(Math.random() * 6);    // 5-10 (same as legendary; mythic only via ascension)
    default:          return 0;
  }
}

/** All set IDs currently in the equipment drop pool. New sets added in P3+
 *  go here so a single equip_set roll can produce items from any of them.
 *  The aspectAffinity tag on each item still gates which set is USEFUL for
 *  the player's current build — uniform drops let players try both. */
const EQUIP_POOL_SETS: Array<'hollow_knight' | 'vault_knight'> = ['hollow_knight', 'vault_knight'];

function pickFromEquipPool(allowLegendary: boolean = true): EquipmentInstance | null {
  // Union of all set pools. Uniform pick across the combined pool so legendary
  // share stays roughly constant (4 legendaries / 16 total = 25%) regardless
  // of how many sets exist.
  let pool: ReturnType<typeof itemsInSet> = [];
  for (const setId of EQUIP_POOL_SETS) {
    pool = pool.concat(itemsInSet(setId));
  }
  if (!allowLegendary) {
    // Per-run cap reached — restrict to rare/epic pieces. Player still gets
    // an item drop (so the run isn't visually empty), but the legendary
    // ceiling holds firm.
    pool = pool.filter((item) => item.rarity !== 'legendary');
  }
  if (pool.length === 0) return null;
  const pick = pool[Math.floor(Math.random() * pool.length)];
  const rolledStats = rollBaseStats(pick);
  const bonusStats = rollBonusStats(pick.rarity);
  return {
    id: randomId('eq'),
    itemId: pick.id,
    upgradeLevel: rollUpgradeOnDrop(pick.rarity),
    rolledStats,
    bonusStats: bonusStats.length > 0 ? bonusStats : undefined,
  };
}

function rollOnce(
  tier: DropTier,
  lootMultiplier: number,
  isBoss: boolean,
  gamblerBonus: number,
  levelOvershoot: number,
  legendaryDropsRemaining: number = Infinity,
): DropRollResult {
  const table = DROP_TABLES[tier];
  const result: DropRollResult = { resources: [], equipment: [] };

  for (const [key, baseChance] of table) {
    const levelBonus = getLevelDropBonus(key, levelOvershoot, baseChance);
    // Gambler-Fallacy soft-pity bonus applies to set-piece drops now that the
    // old `equip_legendary` rarity pool is gone — keeps the skill meaningful.
    const pityBonus = key === 'equip_set' ? gamblerBonus : 0;
    const chance = Math.min(1, (baseChance + levelBonus + pityBonus) * lootMultiplier);
    if (Math.random() >= chance) continue;

    if (key === 'equip_set') {
      // Per-run cap: once exhausted, restrict the HK pool to non-legendary
      // pieces so the player still gets SOMETHING (rare/epic) but doesn't
      // exceed the legendary ceiling.
      const drop = pickFromEquipPool(legendaryDropsRemaining > 0);
      if (drop) result.equipment.push(drop);
    } else {
      result.resources.push({ itemId: key, quantity: 1 });
    }
  }

  // Bosses guarantee at least one equipment drop — but the fallback is
  // ALWAYS non-legendary so a hard-coded uniform 25% legendary chance per
  // boss kill doesn't bypass the actual drop-rate math. Legendaries come
  // from the explicit equip_set roll above; the fallback is the "you killed
  // a boss, you get SOMETHING" floor — meant to deliver a rare or epic.
  if (isBoss && result.equipment.length === 0) {
    const drop = pickFromEquipPool(false); // hard non-legendary
    if (drop) result.equipment.push(drop);
  }
  return result;
}

export function rollTierLoot(
  tier: DropTier,
  lootMultiplier: number = 1,
  isBoss: boolean = false,
  gamblerBonus: number = 0,
  flatDropBonus: number = 0,
  bossUptierChance: number = 0,
  levelOvershoot: number = 0,
  legendaryDropsRemaining: number = Infinity,
): DropRollResult {
  // Clamp the combined loot-multiplier stack to MAX_LOOT_MULTIPLIER so a
  // fully-decked-out player can't blow drop rates past the design intent.
  const clampedMult = Math.min(MAX_LOOT_MULTIPLIER, lootMultiplier + flatDropBonus);
  const rollTier =
    isBoss && bossUptierChance > 0 && Math.random() < bossUptierChance
      ? (NEXT_DROP_TIER[tier] ?? tier)
      : tier;
  return rollOnce(rollTier, clampedMult, isBoss, gamblerBonus, levelOvershoot, legendaryDropsRemaining);
}

/**
 * Caps a list of equipment drops so at most `max` legendary items remain (default 1).
 * When `preferIds` is supplied, items whose itemId matches one of those IDs are kept
 * preferentially over generic-pool legendaries (used so a boss-exclusive drop wins
 * over a randomly-rolled legendary-pool item that fired in the same kill).
 * Non-legendary items are always preserved.
 */
function capLegendaryDrops(
  equipment: EquipmentInstance[],
  max: number = 1,
  preferIds: string[] = [],
): EquipmentInstance[] {
  const isLegendary = (e: EquipmentInstance) => getItem(e.itemId)?.rarity === 'legendary';
  // Stable reorder so preferred-id legendaries get kept first.
  const preferred: EquipmentInstance[] = [];
  const rest: EquipmentInstance[] = [];
  for (const e of equipment) {
    if (isLegendary(e) && preferIds.includes(e.itemId)) preferred.push(e);
    else rest.push(e);
  }
  const ordered = [...preferred, ...rest];
  let legCount = 0;
  const out: EquipmentInstance[] = [];
  for (const e of ordered) {
    if (isLegendary(e)) {
      if (legCount >= max) continue;
      legCount++;
    }
    out.push(e);
  }
  return out;
}

// ── Vault helpers ─────────────────────────────────────────────

/** How many waves a vault has for a player at the given level. */
export function effectiveWaveCount(vault: VaultDef, playerLevel: number): number {
  const overshoot = Math.max(0, playerLevel - vault.minLevel);
  const extra = Math.min(MAX_EXTRA_WAVES, Math.floor(overshoot / 5));
  return vault.waves.length + extra;
}

/** Scale factor applied to enemy HP, XP, and credits. */
export function calcScaledHpMult(vault: VaultDef, playerLevel: number): number {
  const overshoot = Math.max(0, playerLevel - vault.minLevel);
  return Math.min(HP_SCALE_CAP, 1 + overshoot * HP_SCALE_PER_LEVEL);
}

function getEffectiveWave(vault: VaultDef, waveIndex: number) {
  // Extra waves reuse the last base wave's enemy roster
  const idx = Math.min(waveIndex, vault.waves.length - 1);
  return vault.waves[idx];
}

/** Build a fresh ActiveEnemy[] for the given wave, scaled by hpMult. */
function buildActiveEnemiesForWave(
  vault: VaultDef, waveIndex: number, hpMult: number, totalWaves: number,
): ActiveEnemy[] {
  const wave = getEffectiveWave(vault, waveIndex);
  if (!wave) return [];
  // Boss wave (the final wave of the run) spawns ONLY the first enemy as a
  // single big boss fight. Earlier waves keep the multi-mob swarm.
  const isBossWave = waveIndex === totalWaves - 1;
  let enemyIds = isBossWave ? wave.enemies.slice(0, 1) : [...wave.enemies];
  // P3 variety: shuffle non-boss waves so the same enemy types don't always
  // land in the same slot/angle on respawn. Fisher-Yates in-place.
  if (!isBossWave) {
    for (let i = enemyIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [enemyIds[i], enemyIds[j]] = [enemyIds[j], enemyIds[i]];
    }
  }
  // Boss HP bonus — P3-tuned. Vault-Alpha boss was 30 HP (10×3) which a
  // legendary-tier weapon one-shots through. Bumped to ×8 so even VK-blade
  // wielders need 2-3 swings; starter-kit takes 6-8 swings (proper boss feel).
  // Higher-tier vaults (vault_omega's omega_herald at 800 HP base) become
  // 6400 HP, which is endgame-appropriate.
  const bossHpBonus = isBossWave ? 8 : 1;
  const out: ActiveEnemy[] = [];
  for (const enemyId of enemyIds) {
    const def = getEnemy(enemyId);
    if (!def) continue;
    const scaled = Math.round(def.maxHp * hpMult * bossHpBonus);
    out.push({
      instanceId: randomId('en'),
      enemyId,
      hp: scaled,
      maxHp: scaled,
    });
  }
  return out;
}

/**
 * Result of resolving a "current target died" event:
 * - `nextTarget`: a new ActiveEnemy from the same wave, or null if wave cleared
 * - `nextActive`: the activeEnemies array minus the dead one (and minus any
 *    lingering hp<=0 entries)
 * - `waveAdvanced`: true if we spawned a fresh wave because the old one was empty
 * - `nextWaveIndex`: the new wave index after advancing (== old if no advance)
 * - `finished`: true if no more waves remain (player wins the run)
 */
interface KillResolution {
  nextTarget: ActiveEnemy | null;
  nextActive: ActiveEnemy[];
  waveAdvanced: boolean;
  nextWaveIndex: number;
  finished: boolean;
}

function resolveKill(
  active: ActiveEnemy[],
  deadInstanceId: string,
  vault: VaultDef | null,
  waveIndex: number,
  totalWaves: number,
  _hpMult: number,
  isEndless: boolean = false,
): KillResolution {
  const survivors = active.filter((e) => e.instanceId !== deadInstanceId && e.hp > 0);
  if (survivors.length > 0) {
    return {
      nextTarget: survivors[0],
      nextActive: survivors,
      waveAdvanced: false,
      nextWaveIndex: waveIndex,
      finished: false,
    };
  }
  // Wave cleared. Endless runs never "finish" via wave clear — they keep
  // spawning fresh procedural waves. Only flee/defeat (handled elsewhere)
  // end an endless run. For regular vault runs: finished when last wave OR
  // when the vault def is missing entirely (broken save / bad data).
  if (!isEndless && (!vault || waveIndex + 1 >= totalWaves)) {
    return {
      nextTarget: null,
      nextActive: [],
      waveAdvanced: false,
      nextWaveIndex: waveIndex,
      finished: true,
    };
  }
  return {
    nextTarget: null,
    nextActive: [],
    waveAdvanced: false,
    nextWaveIndex: waveIndex,
    finished: false,
  };
}

/** Returns `true` if the targeted enemy is the boss of its wave (= last in wave order). */
function isWaveBoss(vault: VaultDef | undefined | null, waveIndex: number, totalWaves: number, active: ActiveEnemy[], deadInstanceId: string): boolean {
  if (!vault) return false;
  if (waveIndex !== totalWaves - 1) return false;
  // Boss = last alive enemy in the final wave
  const survivors = active.filter((e) => e.instanceId !== deadInstanceId && e.hp > 0);
  return survivors.length === 0;
}


// ── Vault unlock check ────────────────────────────────────────

export interface UnlockCheck {
  unlocked: boolean;
  reasons: Array<{ kind: string; missing: string }>;
}

export function canEnterVault(vault: VaultDef, player: Player): UnlockCheck {
  const reasons: UnlockCheck['reasons'] = [];
  if (player.level < vault.minLevel) {
    reasons.push({ kind: 'level', missing: `Lv ${vault.minLevel} (aktuell ${player.level})` });
  }

  const equipStats = getEquippedStats(player.equipment);
  const baseAttack = 5 + player.level + equipStats.attack;
  const baseDefense = 2 + Math.floor(player.level / 3) + equipStats.defense;
  const baseHp = 50 + (player.level - 1) * 10 + equipStats.health;

  for (const req of vault.requirements) {
    if (req.type === 'level') {
      if (player.level < req.value) reasons.push({ kind: 'level', missing: `Lv ${req.value}` });
    } else if (req.type === 'equipped_slot') {
      if (!player.equipment[req.slot]) reasons.push({ kind: 'slot', missing: req.slot });
    } else if (req.type === 'min_stat') {
      const actual =
        req.stat === 'attack' ? baseAttack :
        req.stat === 'defense' ? baseDefense :
        baseHp;
      if (actual < req.value) {
        reasons.push({ kind: req.stat, missing: `${req.value} (aktuell ${actual})` });
      }
    } else if (req.type === 'equipped_rarity') {
      const min = minEquippedRarity(player.equipment);
      if (!min || rarityRank(min) < rarityRank(req.minRarity)) {
        reasons.push({ kind: 'rarity', missing: req.minRarity });
      }
    } else if (req.type === 'min_power') {
      const power = calcEquippedItemPower(player.equipment);
      if (power < req.value) {
        reasons.push({ kind: 'power', missing: `${req.value} Power (aktuell ${power})` });
      }
    }
  }
  return { unlocked: reasons.length === 0, reasons };
}

// ── Store ─────────────────────────────────────────────────────

export interface TickResult {
  playerDamage: number;
  enemyDamage: number;
  isSpecial: boolean;
  isShieldBash: boolean;
  enemyDied: boolean;
  playerDied: boolean;
  newEnemy: EnemyDef | null;
  newLoot: Array<{ itemId: string; quantity: number }>;
  newEquipmentLoot: EquipmentInstance[];
  reflectDamage: number;
  kineticStacks: number;
  abilityUsed?: import('@/domain/types').SkillId;
  crowdDamage: number;   // extra damage from surviving crowd sprites this tick
  crowdKilled: number;   // how many crowd sprites the player killed this tick
  /** True if this tick rolled a critical hit (player attack only). The UI
   *  reads this to render gold damage-numbers + a punchier SFX. Special /
   *  ability casts already render as "crit-styled" — isCrit ADDS a real
   *  damage-multiplier on top instead of just being a visual override. */
  isCrit: boolean;
  /** Total HP healed this tick from any source (lifesteal, on_kill heal,
   *  Soul-Drain on_special). Lets the UI spawn a green "+N HP" floating
   *  number so the player gets visible feedback for sustain effects. */
  healed: number;
  /** Chain-lightning side hit (legendary_lightning legendaryEffect). Set when the
   * 30% proc fires and a 2nd enemy was struck — UI uses this to draw the chain VFX. */
  chainLightning?: { instanceId: string; damage: number; enemyDied: boolean };
  /** Pyromant Combustion: list of enemies hit by the on-kill explosion AOE.
   * UI uses this to spawn explosion VFX + damage numbers per side target. */
  combustionHits?: Array<{ instanceId: string; damage: number; enemyDied: boolean }>;
  /** Stormcaller Static-Charge: list of enemies hit by the periodic AOE
   *  pulse (every Nth swing). UI uses this to spawn a lightning-ring VFX. */
  staticChargeHits?: Array<{ instanceId: string; damage: number; enemyDied: boolean }>;
  /** Pyromant Burning Hits: per-enemy DoT tick damage applied THIS swing.
   *  UI spawns small orange tick-numbers above each burning enemy so the
   *  player sees the sustained damage instead of just an HP-bar twitch. */
  dotHits?: Array<{ instanceId: string; damage: number }>;
}

export type GameSpeed = 1 | 2 | 4;

interface DungeonStoreState extends CombatState {
  gameSpeed: GameSpeed;

  // ── Multi-enemy room state (Archero-style) ────────────────
  /** All currently spawned enemies in the room. Living + recently-dead are filtered (hp>0). */
  activeEnemies: ActiveEnemy[];
  /** Instance ID of the enemy the player is currently auto-aimed at. */
  targetInstanceId: string | null;
  /** Pyromant Burning Hits — per-enemy damage-over-time entries. Keyed by
   *  ActiveEnemy.instanceId. `dpsAtCast` is locked in at apply-time so
   *  equipment swaps mid-burn don't retroactively rescale; `expiresAt` is
   *  epoch-ms; `lastAppliedAt` lets us deal `dps × elapsed` chunks at each
   *  autoTick instead of needing a separate 1Hz timer (DoT effectively ticks
   *  at weapon speed but the total damage budget stays correct). */
  enemyDoTs: Record<string, { dpsAtCast: number; expiresAt: number; lastAppliedAt: number }>;

  // ── Per-run scaling (computed at startRun) ────────────────
  scaledHpMult: number;     // enemy HP/reward multiplier based on level overshoot
  /** Per-wave attack-damage multiplier, applied to every enemy.attack read
   *  in combat math. Vault runs leave this at 1.0; Endless ramps it up via
   *  calcEndlessAtkMult on each wave-advance. */
  scaledAtkMult: number;
  levelOvershoot: number;   // playerLevel - vault.minLevel (for loot bonus)
  totalWaves: number;       // effective wave count (base + extra)

  // ── Endless mode (Endless Fight arena) ────────────────────
  /** True while the player is in an Endless Fight run. The combat loop is
   *  identical; only advanceToNextRoom and the drop-tier picker react to
   *  this flag. */
  isEndless: boolean;

  // ── Per-run output multipliers ────────────────────────────
  runLootMult: number;   // 1.0 for manual run, 0.80 for farm
  runXpMult: number;     // 1.0 for manual run, 0.75 for farm

  // ── Per-run skill state ───────────────────────────────────
  kineticStacks: number;
  gamblerCounter: number;
  executeCooldown: number;
  deathPreventionUsed: boolean;
  /** Vault-Knight 3pc capstone: epoch-ms until which the 50% DR buff applies.
   * 0 = inactive. Set when the player drops below 30% HP for the first time per run. */
  finalHourActiveUntil: number;
  /** Locks the Final Hour proc to once per vault run. */
  finalHourUsedThisRun: boolean;
  fastAttackTicks: number;
  /** Stormcaller Overcharge stack counter. Increments on every successful
   *  player hit (capped by passive maxStacks values2[]). Each stack adds
   *  `values[]` × AS-mult — the CombatViewStore tick interval reads this
   *  and rescales accordingly. Resets to 0 when the idle window elapses. */
  overchargeStacks: number;
  /** Epoch-ms after which overchargeStacks is force-reset to 0 (4 sec idle
   *  decay). Refreshed on every hit. */
  overchargeExpiresAt: number;
  /** Stormcaller Static-Charge hit counter. Increments on every player hit;
   *  when it reaches the threshold (values2[] from the passive) it triggers
   *  an AOE pulse (values[] × ATK) and resets. */
  staticChargeCounter: number;
  /** Bulwark Frost-Nova self-buff: epoch-ms until which the player has +30%
   *  DEF after casting the active skill. 0 = inactive. Read in autoTick's
   *  damage-mitigation step (effectiveStats.defense × novaBuff). */
  frostNovaDefUntil: number;
  /** Per-run legendary drop counter. Capped at LEGENDARY_RUN_CAP_* — once
   *  reached, further `pickFromEquipPool` calls restrict to non-legendary
   *  pieces. Reset on every startRun / startEndlessRun / startBossRun. */
  runLegendaryCount: number;

  // ── Crowd state (set by the 3D combat view after enemy spawn) ────
  crowdCount: number;
  crowdAttack: number;

  setGameSpeed: (speed: GameSpeed) => void;
  setCrowdState: (count: number, attackPerUnit: number) => void;
  /** Visual layer calls this when the auto-aim picks a different (nearest alive) enemy. */
  setTargetEnemy: (instanceId: string | null) => void;
  /**
   * Spawn the NEXT wave's enemies (after the current one was cleared) — called
   * by the visual layer when it detects the player crossing the threshold
   * into the next room. If no more rooms remain, sets phase='victory'.
   */
  advanceToNextRoom: () => void;
  startRun: (vaultId: string, playerStats: PlayerCombatStats, player: Player, mode?: 'manual' | 'farm') => void;
  startBossRun: (bossId: string, playerStats: PlayerCombatStats, player: Player) => void;
  /** Start an Endless Fight run — procedurally generated waves that never end,
   *  difficulty + drop tier scale with `waveIndex`. Wave reached is recorded
   *  to `player.bestEndlessWave` on flee/defeat. */
  startEndlessRun: (playerStats: PlayerCombatStats, player: Player) => void;
  flee: () => void;
  /**
   * Apply damage from a boss special-attack (charge / AOE / chain). Bypasses
   * autoTick so the boss can hit the player on its own cooldown rather than
   * only on player-attack ticks. If this kills the player, transitions to
   * 'defeat'.
   */
  bossSpecialHit: (amount: number) => void;
  autoTick: (playerStats: PlayerCombatStats, player: Player) => TickResult | null;
  triggerSpecial: (playerStats: PlayerCombatStats, player: Player) => TickResult | null;
  instantClear: (playerStats: PlayerCombatStats, player: Player) => void;
  claimLoot: () => {
    loot: CombatState['loot'];
    equipment: EquipmentInstance[];
    xp: number;
    /** v2: Corrupted Data accrued (was `credits` in v1). */
    corruptedData: number;
    vaultTier: number;
    groupBonus: number;
  };
  reset: () => void;
}

const PER_RUN_RESET = {
  scaledHpMult: 1,
  scaledAtkMult: 1,
  levelOvershoot: 0,
  totalWaves: 3,
  runLootMult: 1.0,
  runXpMult: 1.0,
  kineticStacks: 0,
  gamblerCounter: 0,
  executeCooldown: 0,
  deathPreventionUsed: false,
  finalHourActiveUntil: 0,
  finalHourUsedThisRun: false,
  fastAttackTicks: 0,
  crowdCount: 0,
  crowdAttack: 0,
  isEndless: false,
  enemyDoTs: {} as Record<string, { dpsAtCast: number; expiresAt: number; lastAppliedAt: number }>,
  overchargeStacks: 0,
  overchargeExpiresAt: 0,
  staticChargeCounter: 0,
  frostNovaDefUntil: 0,
  runLegendaryCount: 0,
};

// ── Special attack executor ───────────────────────────────────

function executeSpecialAttack(
  s: DungeonStoreState,
  playerStats: PlayerCombatStats,
  player: Player,
  vault: VaultDef | undefined | null,
  isShieldBash: boolean,
  set: (partial: Partial<DungeonStoreState>) => void,
): TickResult {
  const bonusLootMult = 1 + getEquippedBonusMult(player.equipment, 'loot_chance');
  const bonusXpMult   = 1 + getEquippedBonusMult(player.equipment, 'xp_gain');
  const weaponClass = getEquippedWeaponClass(player);
  const weaponProfile = getWeaponProfile(player);
  const fastTicks = weaponClass === 'fast' ? 5 : 0;

  // Detect active ability skill (fireball / lightning_bolt / frost_lance / frost_nova override the special).
  // hasSkill now also returns true when the id matches player.activeSkillId
  // (P2.B aspect-driven active skills), so this loop covers BOTH the legacy
  // skill-web equip and the new aspect-skill selection.
  // P2.B + P3 ability-class actives. New P3 entries (`phantom_burst`,
  // `engineer_overload`) follow the same multiplier-based instant-hit
  // pattern as the others — engineer_overload additionally ignores 60 %
  // of enemy defense (heaviest armor-pen, see halfDefEnemy below).
  const abilitySkills = ['fireball', 'lightning_bolt', 'frost_lance', 'frost_nova', 'phantom_burst', 'engineer_overload'] as const;
  type AbilitySkill = typeof abilitySkills[number];
  const activeAbility = abilitySkills.find((id) => hasSkill(player, id)) as AbilitySkill | undefined;

  let pDmg: number;
  if (isShieldBash) {
    pDmg = calcShieldBashDamage(playerStats, activeSkillValue(player, 'shield_bash') ?? 2.5);
  } else if (activeAbility) {
    const mult = activeSkillValue(player, activeAbility) ?? 3.5;
    // Fireball ignores 50% of enemy DEF (in addition to weapon armor-pen).
    // Frost-Nova is a self-centered AOE — it doesn't have a "single-target"
    // damage path, but the same multiplier logic is fine: pDmg becomes the
    // value applied to the current target, and the side-AOE pulse below
    // damages the surrounding mob with the same multiplier.
    const halfDefEnemy = activeAbility === 'fireball'
      ? { ...s.currentEnemy!, defense: Math.floor(s.currentEnemy!.defense / 2) }
      : activeAbility === 'engineer_overload'
      ? { ...s.currentEnemy!, defense: Math.floor(s.currentEnemy!.defense * 0.4) }
      : s.currentEnemy!;
    pDmg = Math.round(calcPlayerDamage(playerStats, halfDefEnemy, weaponProfile) * mult);
  } else {
    pDmg = calcSpecialDamage(playerStats, s.currentEnemy!, weaponProfile);
  }
  // P2.A Mystic M3: special_dmg_pct boosts the entire special-cast output
  // (covers shield-bash, ability, and base-special paths uniformly).
  const augSpecialDmg = 1 + totalAugmentValue(player.augments, 'special_dmg_pct');
  if (augSpecialDmg !== 1) pDmg = Math.round(pDmg * augSpecialDmg);

  // P2.B Vampire Soul-Drain (on_special heal): heal %MaxHP whenever a
  // special-cast resolves. Stacks with Feast (on_kill) if the special is
  // also the killing blow. Applied to the player's current HP via the
  // set() below; no separate effect needed.
  const aspectSpecialHealPct = totalAspectValue(player, 'on_special', false, getItem);
  const soulDrainHeal = aspectSpecialHealPct > 0
    ? Math.round(playerStats.maxHp * aspectSpecialHealPct) : 0;

  const newEnemyHp = s.enemyHp - pDmg;
  const enemyDied = newEnemyHp <= 0;
  const phantomReset = hasSetBonus(player.equipment, 'execute_reset');

  // Crowd kills: AoE abilities kill more crowd. Frost-Nova clears the most
  // (true PBAoE radius), Fireball next (splash), Lightning_bolt chains, etc.
  const crowdKilled =
    activeAbility === 'frost_nova'        ? Math.min(6, s.crowdCount)
    : activeAbility === 'fireball'         ? Math.min(5, s.crowdCount)
    : activeAbility === 'phantom_burst'    ? Math.min(4, s.crowdCount)
    : activeAbility === 'lightning_bolt'   ? Math.min(4, s.crowdCount)
    : activeAbility === 'frost_lance'      ? Math.min(3, s.crowdCount)
    : activeAbility === 'engineer_overload' ? Math.min(2, s.crowdCount)
    : Math.min(2, s.crowdCount);
  if (crowdKilled > 0) set({ crowdCount: Math.max(0, s.crowdCount - crowdKilled) });

  const result: TickResult = {
    playerDamage: pDmg, enemyDamage: 0, isSpecial: true, isShieldBash,
    enemyDied, playerDied: false, newEnemy: null,
    newLoot: [], newEquipmentLoot: [],
    reflectDamage: 0, kineticStacks: s.kineticStacks,
    abilityUsed: activeAbility,
    crowdDamage: 0, crowdKilled,
    // Specials already get a 3× damage baseline + ability multipliers —
    // additional crit rolls are skipped to prevent burst-spikes that would
    // make balance unpredictable. Visual "crit-style" already shown via
    // isSpecial; isCrit stays false here.
    isCrit: false,
    healed: soulDrainHeal, // populated only if Vampire Soul-Drain is active
  };

  if (enemyDied) {
    const targetId = s.targetInstanceId!;
    const isBoss = isWaveBoss(vault, s.waveIndex, s.totalWaves, s.activeEnemies, targetId);
    // P2.A Augment: Hunter branch contributes loot-chance and extra-boss-roll.
    const augLootMult = 1 + totalAugmentValue(player.augments, 'loot_chance_pct');
    const augExtraBossRoll = totalAugmentValue(player.augments, 'extra_boss_roll');
    const lootMult = boostMultiplier(player.boosts, 'loot_chance') * augLootMult;
    const flatBonus = getSetBonusValue(player.equipment, 'flat_drop_chance');
    const uptierChance = getSetBonusValue(player.equipment, 'boss_uptier');
    const treasureRolls = (isBoss && hasSkill(player, 'treasure_hunter')
      ? Math.round(activeSkillValue(player, 'treasure_hunter') ?? 2)
      : 1) + (isBoss ? augExtraBossRoll : 0);

    let allResources: DropRollResult['resources'] = [];
    let allEquipment: DropRollResult['equipment'] = [];
    // Soft-pity: add player.legendaryPity (capped 0.30) on top of run gambler.
    const pityBonus = player.legendaryPity ?? 0;
    // Per-run legendary cap — same as autoTick path, threaded into rollTierLoot.
    const runCapSpecial = s.isEndless ? LEGENDARY_RUN_CAP_ENDLESS : LEGENDARY_RUN_CAP_VAULT;
    let legendariesThisRunSpecial = s.runLegendaryCount;
    for (let r = 0; r < treasureRolls; r++) {
      const remaining = Math.max(0, runCapSpecial - legendariesThisRunSpecial);
      const drops = rollTierLoot(
        effectiveDropTier(s, vault), lootMult * s.runLootMult * bonusLootMult, isBoss && r === 0,
        s.gamblerCounter + pityBonus, flatBonus, isBoss ? uptierChance : 0, s.levelOvershoot,
        remaining,
      );
      allResources = [...allResources, ...drops.resources];
      allEquipment = [...allEquipment, ...drops.equipment];
      legendariesThisRunSpecial += drops.equipment.filter(
        (e) => getItem(e.itemId)?.rarity === 'legendary',
      ).length;
    }
    // Cap to at most one legendary per kill (across all treasure rolls).
    allEquipment = capLegendaryDrops(allEquipment, 1);

    const droppedLegendary = allEquipment.some(
      (e) => getItem(e.itemId)?.rarity === 'legendary',
    );
    const newRunLegendaryCountSpecial = s.runLegendaryCount + (droppedLegendary ? 1 : 0);
    // Per-kill pity bump (Farm-button safe — see autoTick for rationale).
    useGameStore.getState().bumpLegendaryPity(droppedLegendary);
    const momentumBonus = hasSkill(player, 'momentum')
      ? Math.round(SPECIAL_MAX * (activeSkillValue(player, 'momentum') ?? 0)) : 0;

    result.newLoot = allResources;
    result.newEquipmentLoot = allEquipment;
    const totalLoot = [...s.loot, ...allResources];
    const totalEquipmentLoot = [...s.equipmentLoot, ...allEquipment];
    const totalXp = s.earnedXp + Math.round(s.currentEnemy!.xpReward * s.scaledHpMult * s.runXpMult * bonusXpMult);
    const cdBase = s.currentEnemy!.corruptedDataReward * s.scaledHpMult * s.runXpMult * bonusXpMult;
    const cdBossBonus = isBoss ? BOSS_CD_MULTIPLIER : 1;
    const totalCorruptedData = s.earnedCorruptedData + Math.round(cdBase * cdBossBonus);

    // P2.B Pyromant Combustion (on_kill explosion): same as autoTick kill
    // path — propagate raw fire damage to all surviving enemies before
    // resolveKill, so the next-target HP reflects the AOE wipe.
    const combustionPct = resolvePassiveById(player, 'pyromant', 'combustion', getItem);
    let postCombustionActive: typeof s.activeEnemies = s.activeEnemies;
    if (combustionPct > 0) {
      const combustionDmg = Math.max(1, Math.round(playerStats.attack * combustionPct));
      const hits: NonNullable<TickResult['combustionHits']> = [];
      postCombustionActive = s.activeEnemies.map((e) => {
        if (e.instanceId === targetId || e.hp <= 0) return e;
        const newHp = Math.max(1, e.hp - combustionDmg);
        hits.push({ instanceId: e.instanceId, damage: combustionDmg, enemyDied: false });
        return { ...e, hp: newHp };
      });
      if (hits.length > 0) result.combustionHits = hits;
    }

    const resolution = resolveKill(postCombustionActive, targetId, vault ?? null, s.waveIndex, s.totalWaves, s.scaledHpMult, s.isEndless);
    // Apply Soul-Drain + Feast heal (on_special + on_kill stacks). Capped
    // at maxHp so over-healing doesn't accumulate. Look up Feast by id —
    // Pyromant Combustion also uses on_kill but means damage, not heal.
    const aspectKillHealPct = resolvePassiveById(player, 'vampire', 'feast', getItem);
    const aspectKillHeal = aspectKillHealPct > 0
      ? Math.round(playerStats.maxHp * aspectKillHealPct) : 0;
    const healedPlayerHp = Math.min(playerStats.maxHp, s.playerHp + soulDrainHeal + aspectKillHeal);
    const baseSet = {
      specialCharge: Math.min(SPECIAL_MAX, momentumBonus),
      fastAttackTicks: fastTicks,
      gamblerCounter: droppedLegendary ? 0 : s.gamblerCounter + GAMBLER_STEP,
      executeCooldown: phantomReset ? 0 : Math.max(0, s.executeCooldown - 1),
      playerHp: healedPlayerHp,
      // Bulwark Frost-Nova self-buff window. Set if this cast was Frost-Nova,
      // preserve any existing window otherwise (don't reset on every special).
      frostNovaDefUntil: activeAbility === 'frost_nova'
        ? Date.now() + 5000 : s.frostNovaDefUntil,
      runLegendaryCount: newRunLegendaryCountSpecial,
    };

    if (resolution.finished) {
      set({
        enemyHp: 0, activeEnemies: [], targetInstanceId: null, currentEnemy: null,
        loot: totalLoot, equipmentLoot: totalEquipmentLoot,
        earnedXp: totalXp, earnedCorruptedData: totalCorruptedData, phase: 'victory', ...baseSet,
      });
    } else if (resolution.nextTarget) {
      const nt = resolution.nextTarget;
      const ntDef = getEnemy(nt.enemyId);
      result.newEnemy = ntDef ?? null;
      set({
        activeEnemies: resolution.nextActive,
        targetInstanceId: nt.instanceId,
        currentEnemy: ntDef ?? null,
        enemyHp: nt.hp, enemyMaxHp: nt.maxHp,
        waveIndex: resolution.nextWaveIndex,
        loot: totalLoot, equipmentLoot: totalEquipmentLoot,
        earnedXp: totalXp, earnedCorruptedData: totalCorruptedData, ...baseSet,
      });
    } else {
      // Wave cleared but more rooms remain — clear active state, wait for the
      // visual layer to detect player crossing into the next room and call
      // `advanceToNextRoom()` to spawn that wave's enemies.
      set({
        activeEnemies: [], targetInstanceId: null, currentEnemy: null,
        enemyHp: 0, enemyMaxHp: 0,
        loot: totalLoot, equipmentLoot: totalEquipmentLoot,
        earnedXp: totalXp, earnedCorruptedData: totalCorruptedData, ...baseSet,
      });
    }
    return result;
  }

  // Apply non-fatal damage to the active enemy AND the mirrored enemyHp field.
  // Soul-Drain heal applies here too (on_special fires regardless of kill).
  const updatedActive = s.activeEnemies.map((e) =>
    e.instanceId === s.targetInstanceId ? { ...e, hp: newEnemyHp } : e,
  );
  const healedHpNonFatal = soulDrainHeal > 0
    ? Math.min(playerStats.maxHp, s.playerHp + soulDrainHeal) : s.playerHp;
  const newFrostNovaDefUntilNonFatal = activeAbility === 'frost_nova'
    ? Date.now() + 5000 : s.frostNovaDefUntil;
  set({
    enemyHp: newEnemyHp, activeEnemies: updatedActive,
    specialCharge: 0, fastAttackTicks: fastTicks,
    executeCooldown: Math.max(0, s.executeCooldown - 1),
    playerHp: healedHpNonFatal,
    frostNovaDefUntil: newFrostNovaDefUntilNonFatal,
  });
  return result;
}

// ── Store ─────────────────────────────────────────────────────

export const useDungeonStore = create<DungeonStoreState>()((set, get) => ({
  ...IDLE_STATE,
  ...PER_RUN_RESET,
  gameSpeed: 1,
  activeEnemies: [],
  targetInstanceId: null,

  setGameSpeed: (speed) => set({ gameSpeed: speed }),
  setCrowdState: (count, attackPerUnit) => set({ crowdCount: count, crowdAttack: attackPerUnit }),

  setTargetEnemy: (instanceId) => {
    const s = get();
    if (instanceId === null) {
      set({ targetInstanceId: null, currentEnemy: null, enemyHp: 0, enemyMaxHp: 0 });
      return;
    }
    const ae = s.activeEnemies.find((e) => e.instanceId === instanceId);
    if (!ae || ae.hp <= 0) return;
    const def = getEnemy(ae.enemyId);
    if (!def) return;
    set({
      targetInstanceId: instanceId,
      currentEnemy: def,
      enemyHp: ae.hp,
      enemyMaxHp: ae.maxHp,
    });
  },

  advanceToNextRoom: () => {
    const s = get();
    if (s.phase !== 'combat' || !s.vaultId) return;
    // Only advance if the current room is actually cleared.
    if (s.activeEnemies.length > 0) return;
    const nextWaveIdx = s.waveIndex + 1;
    // Endless mode bypasses the VaultDef.waves table — waves are generated on
    // the fly via buildEndlessWave with wave-driven HP+ATK scaling.
    let fresh: ActiveEnemy[];
    let nextHpMult = s.scaledHpMult;
    let nextAtkMult = s.scaledAtkMult;
    if (s.isEndless) {
      nextHpMult = calcEndlessHpMult(nextWaveIdx);
      nextAtkMult = calcEndlessAtkMult(nextWaveIdx);
      fresh = buildEndlessWave(nextWaveIdx, nextHpMult);
    } else {
      const vault = getVault(s.vaultId);
      if (!vault) return;
      if (nextWaveIdx >= s.totalWaves) {
        // No more rooms — finalize as victory (resolveKill should have already
        // done this, but as a safety net).
        set({ phase: 'victory' });
        return;
      }
      fresh = buildActiveEnemiesForWave(vault, nextWaveIdx, s.scaledHpMult, s.totalWaves);
    }
    if (fresh.length === 0) {
      set({ phase: 'victory' });
      return;
    }

    // ── Skill-Web: Data-Vampirism heal-on-room-entry ────────────
    // % MaxHP per 100 CD collected this run, capped at 25 % per room.
    // Per node level: 1 % per 100 CD at Lv 1, scales linearly to 5 % at Lv 5.
    const player = useGameStore.getState().player;
    let healedHp = s.playerHp;
    if (player) {
      const dvLevel = nodeLevel(player, 'data_vampirism');
      if (dvLevel > 0 && s.earnedCorruptedData > 0 && s.playerMaxHp > 0) {
        const cdHundreds = Math.floor(s.earnedCorruptedData / 100);
        const healPctRaw = cdHundreds * 0.01 * dvLevel;          // 1 %/Lv per 100 CD
        const healPct = Math.min(0.25, healPctRaw);              // hard cap 25 %
        const healAmount = Math.round(s.playerMaxHp * healPct);
        healedHp = Math.min(s.playerMaxHp, s.playerHp + healAmount);
      }
    }

    const first = fresh[0];
    const def = getEnemy(first.enemyId);
    set({
      activeEnemies: fresh,
      targetInstanceId: first.instanceId,
      currentEnemy: def ?? null,
      enemyHp: first.hp,
      enemyMaxHp: first.maxHp,
      waveIndex: nextWaveIdx,
      playerHp: healedHp,
      scaledHpMult: nextHpMult,
      scaledAtkMult: nextAtkMult,
    });
  },

  startEndlessRun: (playerStats, player) => {
    const hpMult = calcEndlessHpMult(0);
    const atkMult = calcEndlessAtkMult(0);
    const active = buildEndlessWave(0, hpMult);
    if (active.length === 0) return;
    const first = active[0];
    const firstDef = getEnemy(first.enemyId);
    if (!firstDef) return;
    // P2.A Mystic M5: special_start_charge gives the player a head-start on
    // their special bar each run (0..1 = 0..100%). Default 0 = empty.
    const startChargePct = totalAugmentValue(player.augments, 'special_start_charge');
    set({
      phase: 'combat', vaultId: ENDLESS_VAULT_ID,
      waveIndex: 0, enemyIndex: 0,
      playerHp: playerStats.maxHp, playerMaxHp: playerStats.maxHp,
      activeEnemies: active,
      targetInstanceId: first.instanceId,
      currentEnemy: firstDef,
      enemyHp: first.hp, enemyMaxHp: first.maxHp,
      specialCharge: SPECIAL_MAX * startChargePct,
      loot: [], equipmentLoot: [], earnedXp: 0, earnedCorruptedData: 0,
      ...PER_RUN_RESET,
      scaledHpMult: hpMult,
      scaledAtkMult: atkMult,
      levelOvershoot: 0,
      totalWaves: ENDLESS_SENTINEL_WAVES,
      runLootMult: 1.0 * currentStreakMult(player),
      runXpMult: 1.0 * currentStreakMult(player),
      isEndless: true,
      runStartAt: Date.now(),
      runMode: 'endless',
    });
  },

  startRun: (vaultId, playerStats, player, mode = 'manual') => {
    const vault = getVault(vaultId);
    if (!vault) return;

    const hpMult = calcScaledHpMult(vault, player.level);
    const overshoot = Math.max(0, player.level - vault.minLevel);
    const waves = effectiveWaveCount(vault, player.level);

    // Spawn the entire first wave at once (Archero-style).
    const active = buildActiveEnemiesForWave(vault, 0, hpMult, waves);
    if (active.length === 0) return;
    const initialTarget = active[0];
    const initialDef = getEnemy(initialTarget.enemyId);
    if (!initialDef) return;

    const startChargePct = totalAugmentValue(player.augments, 'special_start_charge');
    set({
      phase: 'combat', vaultId,
      waveIndex: 0, enemyIndex: 0,
      playerHp: playerStats.maxHp, playerMaxHp: playerStats.maxHp,
      activeEnemies: active,
      targetInstanceId: initialTarget.instanceId,
      currentEnemy: initialDef,
      enemyHp: initialTarget.hp, enemyMaxHp: initialTarget.maxHp,
      specialCharge: SPECIAL_MAX * startChargePct,
      loot: [], equipmentLoot: [], earnedXp: 0, earnedCorruptedData: 0,
      ...PER_RUN_RESET,
      scaledHpMult: hpMult,
      levelOvershoot: overshoot,
      totalWaves: waves,
      // Run-streak engagement bonus: count of active runs in the last
      // 30 min maps to a multiplier (1.00× → 1.30×). Stacks on top of
      // farm/manual base mult; pushStreakEntry runs at claimLoot to grow
      // the history for the NEXT run.
      runLootMult: (mode === 'farm' ? 0.80 : 1.0) * currentStreakMult(player),
      runXpMult:   (mode === 'farm' ? 0.75 : 1.0) * currentStreakMult(player),
      runStartAt: Date.now(),
      runMode: mode,
    });
    // FTUE: first vault entry → step 1 (auto-combat hint). Idempotent.
    useGameStore.getState().advanceTutorial(1);
  },

  startBossRun: (bossId, playerStats, player) => {
    const boss = getBoss(bossId);
    if (!boss) return;
    const baseEnemy = getEnemy(boss.baseEnemyId);
    if (!baseEnemy) return;
    // Block if on cooldown
    const lastKill = (player.lastBossKillAt ?? {})[bossId] ?? 0;
    if (Date.now() - lastKill < boss.cooldownSec * 1000) return;

    const scaledEnemy: import('@/domain/types').EnemyDef = {
      ...baseEnemy,
      maxHp: Math.round(baseEnemy.maxHp * boss.hpMult),
      attack: Math.round(baseEnemy.attack * boss.atkMult),
      defense: Math.round(baseEnemy.defense * boss.defMult),
      xpReward: Math.round(baseEnemy.xpReward * boss.hpMult),
      creditReward: Math.round(baseEnemy.creditReward * boss.hpMult),
      corruptedDataReward: Math.round(baseEnemy.corruptedDataReward * boss.hpMult),
    };

    // Pre-roll boss exclusive loot — stored in equipmentLoot so victory screen shows it
    const exclusiveEquipment: EquipmentInstance[] = [];
    const exclusiveLoot: Array<{itemId: string; quantity: number}> = [];
    for (let i = 0; i < boss.guaranteedDrops; i++) {
      const drop = rollOnce(boss.bonusDropTier, 1, true, 0, 0);
      exclusiveEquipment.push(...drop.equipment);
      exclusiveLoot.push(...drop.resources);
    }
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const isFirstToday = lastKill < startOfToday.getTime();
    const baseRollChance = isFirstToday ? 0.40 : 0.15;
    const shouldDropExclusive = DEBUG_FORCE_BOSS_EXCLUSIVE
      ? boss.exclusiveItemIds.length > 0
      : Math.random() < baseRollChance && boss.exclusiveItemIds.length > 0;
    if (shouldDropExclusive) {
      let exclusiveId: string;
      if (DEBUG_FORCE_BOSS_EXCLUSIVE) {
        const idx = (debugExclusiveCounter[bossId] ?? 0) % boss.exclusiveItemIds.length;
        exclusiveId = boss.exclusiveItemIds[idx];
        debugExclusiveCounter[bossId] = (debugExclusiveCounter[bossId] ?? 0) + 1;
      } else {
        exclusiveId = boss.exclusiveItemIds[Math.floor(Math.random() * boss.exclusiveItemIds.length)];
      }
      const def = getItem(exclusiveId);
      if (def) {
        exclusiveEquipment.push({
          id: randomId('eq'),
          itemId: exclusiveId,
          upgradeLevel: rollUpgradeOnDrop(def.rarity as Rarity),
          rolledStats: rollBaseStats(def),
          bonusStats: rollBonusStats(def.rarity as Rarity).length > 0 ? rollBonusStats(def.rarity as Rarity) : undefined,
        });
      }
    }

    // Cap to at most one legendary per boss kill. Boss-exclusive item wins over
    // any generic-pool legendary that rolled in the bonus tier loop above.
    const cappedExclusiveEquipment = capLegendaryDrops(exclusiveEquipment, 1, boss.exclusiveItemIds);

    const bossActive: ActiveEnemy = {
      instanceId: randomId('en'),
      enemyId: scaledEnemy.id,
      hp: scaledEnemy.maxHp,
      maxHp: scaledEnemy.maxHp,
    };
    set({
      phase: 'combat',
      vaultId: bossId,
      waveIndex: 0, enemyIndex: 0,
      playerHp: playerStats.maxHp, playerMaxHp: playerStats.maxHp,
      activeEnemies: [bossActive],
      targetInstanceId: bossActive.instanceId,
      enemyHp: scaledEnemy.maxHp, enemyMaxHp: scaledEnemy.maxHp,
      currentEnemy: scaledEnemy,
      specialCharge: SPECIAL_MAX * totalAugmentValue(player.augments, 'special_start_charge'),
      loot: exclusiveLoot, equipmentLoot: cappedExclusiveEquipment,
      earnedXp: scaledEnemy.xpReward, earnedCorruptedData: scaledEnemy.corruptedDataReward,
      ...PER_RUN_RESET,
      totalWaves: 1,
      runLootMult: 1.0 * currentStreakMult(player),
      runXpMult: 1.0 * currentStreakMult(player),
      runStartAt: Date.now(),
      runMode: 'boss',
    });
  },

  // Exit-vault: cleanly drop back to idle. Discards in-progress loot/XP and
  // clears all combat state (activeEnemies, kineticStacks, cooldowns) — same
  // shape as reset(), just from any phase. No HP/credit penalty: the player
  // just chooses to walk out. Used by the "Vault verlassen" button.
  flee: () => {
    const s = get();
    // Endless mode: a "flee" is the canonical way to END an endless run
    // (there is no natural wave-victory). The player has EARNED everything
    // collected so far, so we route through 'victory' phase to surface the
    // existing claim-loot UI. The 1-based waveIndex+1 is what we score and
    // persist as bestEndlessWave.
    if (s.isEndless) {
      useGameStore.getState().recordEndlessWave(s.waveIndex + 1);
      emitRunTelemetry('flee', s);
      set({
        phase: 'victory',
        activeEnemies: [],
        targetInstanceId: null,
        enemyHp: 0,
        currentEnemy: null,
        // loot / equipmentLoot / earnedXp / earnedCorruptedData stay as-is
        // — claimLoot() reads them from state and transfers to gameStore.
      });
      return;
    }
    emitRunTelemetry('flee', s);
    set({ ...IDLE_STATE, ...PER_RUN_RESET, phase: 'idle', activeEnemies: [], targetInstanceId: null });
  },

  bossSpecialHit: (amount) => {
    const s = get();
    if (s.phase !== 'combat') return;
    const newHp = Math.max(0, s.playerHp - Math.max(0, Math.round(amount)));
    if (newHp <= 0) {
      // Death — record telemetry as 'defeat' regardless of endless/regular,
      // even though endless routes the UI through 'victory' for the claim
      // step (the run STILL ended via dying, not voluntary exit).
      emitRunTelemetry('defeat', s);
      if (s.isEndless) {
        useGameStore.getState().recordEndlessWave(s.waveIndex + 1);
        set({ playerHp: 0, phase: 'victory', activeEnemies: [], targetInstanceId: null, currentEnemy: null });
        return;
      }
      set({ playerHp: 0, phase: 'defeat' });
      return;
    }
    set({ playerHp: newHp });
  },
  autoTick: (playerStats, player) => {
    // S-1 / S-2 guards: bail out if combat-state is incomplete. Without these
    // an undefined playerStats / weaponProfile cascades into NaN damage and
    // breaks the special-charge math for the rest of the run.
    if (!playerStats || !player) return null;
    const sPre = get();
    if (sPre.phase !== 'combat' || !sPre.currentEnemy || !sPre.vaultId) return null;

    // ── P3 Engineer hp_regen tick ─────────────────────────────
    // Heals % of maxHp scaled to real-elapsed-seconds since the last
    // regen tick, NOT per-autoTick — otherwise faster weapons would
    // over-heal because they fire more ticks per second. Heal applied
    // pre-damage so it can save the player on a tick where they'd
    // otherwise drop below 0.
    {
      const regenPct = totalAspectValue(player, 'hp_regen', false, getItem);
      if (regenPct > 0 && sPre.playerHp > 0 && sPre.playerHp < playerStats.maxHp) {
        const now = Date.now();
        const last = sPre.lastHpRegenAt ?? now;
        const elapsedSec = Math.max(0, (now - last) / 1000);
        if (elapsedSec > 0) {
          const healed = Math.round(playerStats.maxHp * regenPct * elapsedSec);
          if (healed > 0) {
            const newHp = Math.min(playerStats.maxHp, sPre.playerHp + healed);
            set({ playerHp: newHp, lastHpRegenAt: now });
          } else {
            set({ lastHpRegenAt: now });
          }
        }
      } else if (regenPct > 0 && sPre.lastHpRegenAt === undefined) {
        // First tick after entering combat with regen active — seed the
        // timestamp so the first real heal calculates from "now", not
        // from epoch 0 (which would dump 1.7-billion-second worth of
        // healing into the first tick).
        set({ lastHpRegenAt: Date.now() });
      }
    }

    // ── P2.B Pyromant Burning Hits: DoT tick + refresh ────────
    // Ticked at the top of every autoTick: each enemy with an active DoT
    // takes `dpsAtCast × elapsedSec` damage since its last application (cap
    // at 1 HP so DoT-only kills don't slip past resolveKill — the next
    // direct swing finishes them). Then the CURRENT target's DoT is
    // refreshed using the player's burning_hits passive (if slotted). The
    // updated state is committed before damage calculations continue, so
    // newEnemyHp etc. all see the post-DoT HP values.
    let dotHitsThisTick: NonNullable<TickResult['dotHits']> | null = null;
    {
      const burnPct = resolvePassiveById(player, 'pyromant', 'burning_hits', getItem);
      const burnDurSec = resolvePassiveById(player, 'pyromant', 'burning_hits', getItem, true);
      const now = Date.now();
      const newDots: typeof sPre.enemyDoTs = {};
      const dotHitsBuf: NonNullable<TickResult['dotHits']> = [];
      const updatedDotActive = sPre.activeEnemies.map((e) => {
        const dot = sPre.enemyDoTs[e.instanceId];
        if (!dot) return e;
        // Damage = dpsAtCast × seconds since last application, clamped to
        // (lastAppliedAt, min(now, expiresAt)] so expiry caps the budget.
        const tickEndAt = Math.min(now, dot.expiresAt);
        const elapsed = Math.max(0, tickEndAt - dot.lastAppliedAt) / 1000;
        const dotDmg = elapsed > 0 ? Math.max(1, Math.round(dot.dpsAtCast * elapsed)) : 0;
        const newHp = dotDmg > 0 ? Math.max(1, e.hp - dotDmg) : e.hp;
        if (dotDmg > 0) dotHitsBuf.push({ instanceId: e.instanceId, damage: dotDmg });
        if (now < dot.expiresAt) {
          // Carry forward with updated lastAppliedAt so future ticks bill
          // from this point.
          newDots[e.instanceId] = { ...dot, lastAppliedAt: now };
        }
        return dotDmg > 0 ? { ...e, hp: newHp } : e;
      });
      if (dotHitsBuf.length > 0) dotHitsThisTick = dotHitsBuf;
      // Refresh DoT on the current target if Pyromant Burning Hits is active.
      const targetId = sPre.targetInstanceId;
      if (burnPct > 0 && burnDurSec > 0 && targetId) {
        newDots[targetId] = {
          dpsAtCast: Math.max(1, Math.round(playerStats.attack * burnPct)),
          expiresAt: now + burnDurSec * 1000,
          lastAppliedAt: now,
        };
      }
      // Mirror target HP onto enemyHp so the rest of autoTick reads the
      // post-DoT target HP.
      const targetEnemy = targetId ? updatedDotActive.find((e) => e.instanceId === targetId) : null;
      set({
        activeEnemies: updatedDotActive,
        enemyDoTs: newDots,
        enemyHp: targetEnemy ? targetEnemy.hp : sPre.enemyHp,
      });
    }

    const s = get();
    // Re-narrow after the DoT-tick set(). The DoT block only mutates
    // activeEnemies/enemyDoTs/enemyHp so phase + currentEnemy + vaultId are
    // unchanged, but TS doesn't carry narrowing across set/get boundaries.
    if (s.phase !== 'combat' || !s.currentEnemy || !s.vaultId) return null;
    const vault = getVault(s.vaultId); // null for boss runs — handled gracefully below
    const bonusLootMult = 1 + getEquippedBonusMult(player.equipment, 'loot_chance');
    const bonusXpMult   = 1 + getEquippedBonusMult(player.equipment, 'xp_gain');

    const weaponProfile = getWeaponProfile(player);
    if (!weaponProfile) return null;
    // v2 combat: every tick = ONE swing (CombatViewStore3D ticks at a weapon-speed-
    // dependent interval — Fast ticks faster, Heavy slower). Each swing applies full
    // damageMult-scaled damage. Removes the accumulator + animation-skip bug.
    const fastMult = s.fastAttackTicks > 0 ? 2 : 1;
    const newFastTicks = Math.max(0, s.fastAttackTicks - 1);

    // Bulwark Frost-Nova grants +30% DEF for 5 sec after the cast. Applied
    // as a multiplier on the already-summed (level + kinetic + equipment)
    // defense so it scales with the player's actual mitigation pool.
    const novaDefMult = s.frostNovaDefUntil > Date.now() ? 1.30 : 1.0;
    const effectiveStats: PlayerCombatStats = {
      ...playerStats,
      defense: Math.round((playerStats.defense + s.kineticStacks) * novaDefMult),
    };

    const pDmgPerHit = calcPlayerDamage(effectiveStats, s.currentEnemy, weaponProfile);
    let pDmg = pDmgPerHit * fastMult;
    // Legendary: Arcane Focus (legendary_staff) — +50% damage on the FIRST strike of
    // a fresh enemy. The "first strike" is detected by the target still being at full HP.
    if (s.enemyHp >= s.enemyMaxHp
        && hasLegendaryEffect(player.equipment, 'arcane_focus')) {
      pDmg = Math.round(pDmg * 1.5);
    }
    // Glitch-Perk: Overloaded — 20 % per stack to triple damage. MULTIPLICATIVE
    // stacking: chance = 1 − (1 − 0.20)^stacks. At 12 stacks ≈ 93 %, never 100 %.
    const overloadedStacks = countGlitchPerkStacks(player.equipment, 'overloaded');
    if (overloadedStacks > 0) {
      const overloadedChance = 1 - Math.pow(1 - 0.20, overloadedStacks);
      if (Math.random() < overloadedChance) pDmg = Math.round(pDmg * 3);
    }
    // ── P2.B Stormcaller Overcharge (attack_speed_stack) ─────
    // Spec is "+N% AS per stack" but a true AS-mult requires re-architecting
    // the CombatViewStore tick interval (setInterval → setTimeout chain).
    // For DPS-parity we apply it as a damage-multiplier instead: the player
    // sees escalating numbers as stacks build, matching the same DPS budget.
    // Future VFX session will swap this for real AS-mult once we own the
    // setTimeout-chain refactor. Decay if idle window elapsed.
    const overchargeNow = Date.now();
    let newOverchargeStacks = overchargeNow > s.overchargeExpiresAt ? 0 : s.overchargeStacks;
    const overchargePerStack = resolvePassiveById(player, 'stormcaller', 'overcharge', getItem);
    const overchargeMaxStacks = Math.round(resolvePassiveById(player, 'stormcaller', 'overcharge', getItem, true));
    if (newOverchargeStacks > 0 && overchargePerStack > 0) {
      pDmg = Math.round(pDmg * (1 + newOverchargeStacks * overchargePerStack));
    }

    // ── Crit-Roll (P2.A.2) ────────────────────────────────────
    // Rolls AFTER overloaded so the crit applies on top of the proc'd
    // damage. Crit-mult stacks multiplicatively with all other modifiers.
    // Default critChance is 0 — only fires when an augment/aspect grants it.
    let isCrit = false;
    if (effectiveStats.critChance > 0 && Math.random() < effectiveStats.critChance) {
      pDmg = Math.round(pDmg * effectiveStats.critMult);
      isCrit = true;
    }
    // Bump Overcharge stacks for THIS hit (after damage applied). Refreshes
    // the 4-sec decay window. Capped at the passive's max-stacks (values2[]).
    if (overchargePerStack > 0 && overchargeMaxStacks > 0) {
      newOverchargeStacks = Math.min(overchargeMaxStacks, newOverchargeStacks + 1);
    }
    const newOverchargeExpiresAt = overchargePerStack > 0 ? overchargeNow + 4000 : 0;

    // ── P2.B Stormcaller Static-Charge (every Nth hit AOE pulse) ─
    // Counter bumps on every swing. When it reaches the threshold (values2
    // from the passive), an AOE pulse hits all surviving enemies except the
    // current target, dealing `values × ATK` raw lightning damage (DEF
    // bypassed — "static" flavor). Cap at 1 HP so pulse-kills are deferred
    // to the next direct swing, same pattern as Combustion / chain_lightning.
    const staticChargePct = resolvePassiveById(player, 'stormcaller', 'static_charge', getItem);
    const staticChargeThreshold = Math.round(resolvePassiveById(player, 'stormcaller', 'static_charge', getItem, true));
    let newStaticCounter = staticChargePct > 0 ? s.staticChargeCounter + 1 : s.staticChargeCounter;
    let staticChargedActive: typeof s.activeEnemies = s.activeEnemies;
    let pendingStaticHits: NonNullable<TickResult['staticChargeHits']> | null = null;
    if (
      staticChargePct > 0
      && staticChargeThreshold > 0
      && newStaticCounter >= staticChargeThreshold
    ) {
      const pulseDmg = Math.max(1, Math.round(effectiveStats.attack * staticChargePct));
      const pulseHits: NonNullable<TickResult['staticChargeHits']> = [];
      staticChargedActive = s.activeEnemies.map((e) => {
        if (e.instanceId === s.targetInstanceId || e.hp <= 0) return e;
        const newHp = Math.max(1, e.hp - pulseDmg);
        pulseHits.push({ instanceId: e.instanceId, damage: pulseDmg, enemyDied: false });
        return { ...e, hp: newHp };
      });
      if (pulseHits.length > 0) pendingStaticHits = pulseHits;
      newStaticCounter = 0;
    }

    let eDmg = calcEnemyDamage(s.currentEnemy, effectiveStats, s.scaledAtkMult);
    // Glitch-Perk: Hollow — 10 % dodge per stack. MULTIPLICATIVE stacking
    // (diminishing returns). chance = 1 − (1 − 0.10)^stacks. At 12 stacks ≈ 71.7 %,
    // never reaches 100 % so the player can't become invulnerable.
    const hollowStacks = countGlitchPerkStacks(player.equipment, 'hollow');
    if (hollowStacks > 0) {
      const dodgeChance = 1 - Math.pow(1 - 0.10, hollowStacks);
      if (Math.random() < dodgeChance) eDmg = 0;
    }
    // Vault-Knight Set capstone: "Final Hour". Below 30% HP for the first time per
    // run, activate a 5-second 50% damage-reduction window. Self-mutating because
    // multiple ticks within the buff window all share the same (newFinalHour*) values.
    let newFinalHourActiveUntil = s.finalHourActiveUntil;
    let newFinalHourUsedThisRun = s.finalHourUsedThisRun;
    const now = Date.now();
    const hpRatio = s.playerHp / Math.max(1, effectiveStats.maxHp);
    if (
      hpRatio < 0.30
      && !newFinalHourUsedThisRun
      && hasSetBonus(player.equipment, 'final_hour_dr')
    ) {
      newFinalHourActiveUntil = now + 5000;
      newFinalHourUsedThisRun = true;
    }
    if (now < newFinalHourActiveUntil) {
      const dr = getSetBonusValue(player.equipment, 'final_hour_dr');
      eDmg = Math.round(eDmg * (1 - dr));
    }

    const kineticGain = hasSkill(player, 'kinetic_absorption')
      ? Math.round(activeSkillValue(player, 'kinetic_absorption') ?? 1) : 0;
    const newKineticStacks = Math.min(KINETIC_MAX_STACKS,
      s.kineticStacks + (eDmg > 0 ? kineticGain : 0));

    // P2.A Mystic: special_charge_pct boosts the charge accumulated per tick.
    const augChargeMult = 1 + totalAugmentValue(player.augments, 'special_charge_pct');
    const chargePerTick = (SPECIAL_CHARGE_RATE_PER_SEC / Math.max(0.1, weaponProfile.attackSpeed)) * augChargeMult;
    let newSpecial = Math.min(SPECIAL_MAX, s.specialCharge + chargePerTick);
    const newExecCD = Math.max(0, s.executeCooldown - 1);

    // ── Auto-cast special when charged ───────────────────────
    if (newSpecial >= SPECIAL_MAX) {
      const useShieldBash = hasSkill(player, 'shield_bash') && s.executeCooldown === 0;
      set({ kineticStacks: newKineticStacks, fastAttackTicks: newFastTicks, executeCooldown: newExecCD });
      const updatedS = { ...get(), kineticStacks: newKineticStacks };
      return executeSpecialAttack(updatedS as DungeonStoreState, effectiveStats, player, vault, useShieldBash, set);
    }

    // ── Execute skill ─────────────────────────────────────────
    const execValue = activeSkillValue(player, 'execute');
    if (execValue !== null && s.executeCooldown === 0) {
      const targetId = s.targetInstanceId!;
      const isBossEnemy = isWaveBoss(vault, s.waveIndex, s.totalWaves, s.activeEnemies, targetId);
      const threshold = execValue * (isBossEnemy ? 0.5 : 1.0);
      if (s.enemyHp / s.enemyMaxHp < threshold) {
        const phantomReset = hasSetBonus(player.equipment, 'execute_reset');
        const augLootMult = 1 + totalAugmentValue(player.augments, 'loot_chance_pct');
        const lootMult = boostMultiplier(player.boosts, 'loot_chance') * augLootMult;
        const flatBonus = getSetBonusValue(player.equipment, 'flat_drop_chance');
        const uptierChance = getSetBonusValue(player.equipment, 'boss_uptier');
        // Per-run legendary cap for the execute-kill path (single roll, so
        // remaining is computed from s.runLegendaryCount directly).
        const runCapExec = s.isEndless ? LEGENDARY_RUN_CAP_ENDLESS : LEGENDARY_RUN_CAP_VAULT;
        const remainingExec = Math.max(0, runCapExec - s.runLegendaryCount);
        const drops = rollTierLoot(
          effectiveDropTier(s, vault), lootMult * s.runLootMult * bonusLootMult, isBossEnemy,
          s.gamblerCounter + (player.legendaryPity ?? 0), flatBonus, uptierChance, s.levelOvershoot,
          remainingExec,
        );
        // Cap to at most one legendary per kill.
        drops.equipment = capLegendaryDrops(drops.equipment, 1);
        const droppedLegendary = drops.equipment.some(
          (e) => getItem(e.itemId)?.rarity === 'legendary',
        );
        const newRunLegendaryCountExec = s.runLegendaryCount + (droppedLegendary ? 1 : 0);
        // Per-kill pity bump (Farm-button safe — see autoTick for rationale).
        useGameStore.getState().bumpLegendaryPity(droppedLegendary);
        const momentumBonus = hasSkill(player, 'momentum')
          ? Math.round(SPECIAL_MAX * (activeSkillValue(player, 'momentum') ?? 0)) : 0;

        const totalLoot = [...s.loot, ...drops.resources];
        const totalEquipmentLoot = [...s.equipmentLoot, ...drops.equipment];
        const totalXp = s.earnedXp + Math.round(s.currentEnemy.xpReward * s.scaledHpMult * s.runXpMult * bonusXpMult);
        const cdBaseExec = s.currentEnemy.corruptedDataReward * s.scaledHpMult * s.runXpMult * bonusXpMult;
        const cdBossBonusExec = isBossEnemy ? BOSS_CD_MULTIPLIER : 1;
        const totalCorruptedData = s.earnedCorruptedData + Math.round(cdBaseExec * cdBossBonusExec);
        const resolution = resolveKill(s.activeEnemies, targetId, vault ?? null, s.waveIndex, s.totalWaves, s.scaledHpMult, s.isEndless);
        const baseSet = {
          kineticStacks: newKineticStacks, fastAttackTicks: newFastTicks,
          specialCharge: Math.min(SPECIAL_MAX, newSpecial + momentumBonus),
          executeCooldown: phantomReset ? 0 : 999,
          gamblerCounter: droppedLegendary ? 0 : s.gamblerCounter + GAMBLER_STEP,
          finalHourActiveUntil: newFinalHourActiveUntil,
          finalHourUsedThisRun: newFinalHourUsedThisRun,
          runLegendaryCount: newRunLegendaryCountExec,
        };
        const result: TickResult = {
          playerDamage: s.enemyHp, enemyDamage: 0, isSpecial: false, isShieldBash: false,
          enemyDied: true, playerDied: false, newEnemy: null,
          newLoot: drops.resources, newEquipmentLoot: drops.equipment,
          reflectDamage: 0, kineticStacks: newKineticStacks,
          crowdDamage: 0, crowdKilled: 0,
          isCrit: false,  // instant-clear is a deterministic one-shot, no crit roll
          healed: 0,
        };
        if (resolution.finished) {
          set({
            enemyHp: 0, activeEnemies: [], targetInstanceId: null, currentEnemy: null,
            loot: totalLoot, equipmentLoot: totalEquipmentLoot,
            earnedXp: totalXp, earnedCorruptedData: totalCorruptedData, phase: 'victory', ...baseSet,
          });
        } else if (resolution.nextTarget) {
          const nt = resolution.nextTarget;
          const ntDef = getEnemy(nt.enemyId);
          result.newEnemy = ntDef ?? null;
          set({
            activeEnemies: resolution.nextActive,
            targetInstanceId: nt.instanceId,
            currentEnemy: ntDef ?? null,
            enemyHp: nt.hp, enemyMaxHp: nt.maxHp,
            waveIndex: resolution.nextWaveIndex,
            loot: totalLoot, equipmentLoot: totalEquipmentLoot,
            earnedXp: totalXp, earnedCorruptedData: totalCorruptedData, ...baseSet,
          });
        } else {
          set({
            activeEnemies: [], targetInstanceId: null, currentEnemy: null,
            enemyHp: 0, enemyMaxHp: 0,
            loot: totalLoot, equipmentLoot: totalEquipmentLoot,
            earnedXp: totalXp, earnedCorruptedData: totalCorruptedData, ...baseSet,
          });
        }
        return result;
      }
    }

    // ── Normal tick ───────────────────────────────────────────
    const newEnemyHp = s.enemyHp - pDmg;

    // Crowd pressure: surviving crowd sprites deal extra damage
    const crowdDamage = Math.round(s.crowdCount * s.crowdAttack);
    // 40% chance to kill 1 crowd sprite per normal attack
    const crowdKilled = s.crowdCount > 0 && Math.random() < 0.4 ? 1 : 0;
    if (crowdKilled > 0) set({ crowdCount: Math.max(0, s.crowdCount - crowdKilled) });

    // Glitch-Perk: Vampiric — heal 3 % of damage dealt this tick per stack.
    const vampiricStacks = countGlitchPerkStacks(player.equipment, 'vampiric');
    const glitchLifesteal = vampiricStacks > 0 && pDmg > 0
      ? Math.round(pDmg * 0.03 * vampiricStacks) : 0;
    // P2.B Vampire Blood-Tap passive: % heal of damage dealt. Stacks with
    // the Glitch-Perk lifesteal so a fully-built Vampire can roll both
    // sources for serious sustain.
    const aspectLifestealPct = totalAspectValue(player, 'lifesteal', false, getItem);
    const aspectLifesteal = aspectLifestealPct > 0 && pDmg > 0
      ? Math.round(pDmg * aspectLifestealPct) : 0;
    const lifesteal = glitchLifesteal + aspectLifesteal;
    const enemyDied = newEnemyHp <= 0;
    // P2.B Vampire Feast (on_kill heal): heal %MaxHP when the player gets a
    // kill THIS tick. Stacks with lifesteal in the same HP cap. Looked up by
    // passive-id so Pyromant Combustion (same effectKind) is not treated as heal.
    const aspectKillHealPct = enemyDied
      ? resolvePassiveById(player, 'vampire', 'feast', getItem) : 0;
    const aspectKillHeal = aspectKillHealPct > 0
      ? Math.round(effectiveStats.maxHp * aspectKillHealPct) : 0;
    const newPlayerHp = Math.min(
      effectiveStats.maxHp,
      s.playerHp - eDmg - crowdDamage + lifesteal + aspectKillHeal,
    );
    const playerDied = newPlayerHp <= 0;

    const mitigated = Math.max(0, s.currentEnemy.attack * s.scaledAtkMult - eDmg);
    // P2.B Bulwark Thorns (on_hit reflect): % of mitigated damage reflected
    // back at attacker. Stacks ON TOP of the existing Vault-Knight 4pc set.
    const setReflectPct = hasSetBonus(player.equipment, 'reflect_dmg')
      ? getSetBonusValue(player.equipment, 'reflect_dmg') : 0;
    const aspectReflectPct = totalAspectValue(player, 'on_hit', false, getItem);
    const totalReflectPct = setReflectPct + aspectReflectPct;
    const reflectDmg = totalReflectPct > 0 ? Math.round(mitigated * totalReflectPct) : 0;
    const finalEnemyHp = enemyDied ? newEnemyHp : newEnemyHp - reflectDmg;

    const result: TickResult = {
      playerDamage: pDmg, enemyDamage: eDmg, isSpecial: false, isShieldBash: false,
      enemyDied: finalEnemyHp <= 0, playerDied, newEnemy: null,
      newLoot: [], newEquipmentLoot: [],
      reflectDamage: reflectDmg, kineticStacks: newKineticStacks,
      crowdDamage, crowdKilled,
      isCrit,
      // Vampire Blood-Tap (per-hit lifesteal) + Feast (on_kill heal) — both
      // flow into the same `lifesteal + aspectKillHeal` sum.
      healed: lifesteal + aspectKillHeal,
    };
    // Static-Charge pulse attached here so VFX can render the AOE lightning
    // ring this tick. Hits were computed earlier (post-Crit) before this point.
    if (pendingStaticHits) result.staticChargeHits = pendingStaticHits;
    // DoT tick damage from the top-of-autoTick burning_hits pass — attached
    // here so the UI spawns floating tick-numbers for the burn-feedback.
    if (dotHitsThisTick) result.dotHits = dotHitsThisTick;

    // ── Death prevention ──────────────────────────────────────
    // Triggers on either:
    //   - legendary 'death_prevention' (Wächter-Amulett)
    //   - vault 6pc 'second_wind' set bonus (full Vault-Knight set)
    // Both share the same one-revive-per-run flag — stacking the two would be
    // too forgiving and the player wouldn't feel the death pressure that
    // makes vaults tense in the first place.
    const secondWindRevive = hasSetBonus(player.equipment, 'second_wind')
      ? getSetBonusValue(player.equipment, 'second_wind') : 0;
    const revivePct = hasLegendaryEffect(player.equipment, 'death_prevention')
      ? 0.5 : secondWindRevive;
    if (playerDied && !s.deathPreventionUsed && revivePct > 0) {
      const revivedHp = Math.round(effectiveStats.maxHp * revivePct);
      set({
        playerHp: revivedHp, deathPreventionUsed: true,
        kineticStacks: newKineticStacks, fastAttackTicks: newFastTicks,
        specialCharge: newSpecial, executeCooldown: newExecCD,
        enemyHp: finalEnemyHp <= 0 ? 0 : finalEnemyHp,
      });
      result.playerDied = false;
      if (finalEnemyHp <= 0) result.enemyDied = true;
      if (!result.enemyDied) return result;
    }

    if (playerDied && result.playerDied !== false) {
      emitRunTelemetry('defeat', s);
      if (s.isEndless) {
        // Endless death: salvage the loot (route through victory phase).
        // Same rationale as bossSpecialHit — the run is gone but the items
        // collected so far are claim-able via the existing UI.
        useGameStore.getState().recordEndlessWave(s.waveIndex + 1);
        set({
          playerHp: 0, phase: 'victory', kineticStacks: newKineticStacks,
          activeEnemies: [], targetInstanceId: null, currentEnemy: null,
        });
        return result;
      }
      set({ playerHp: 0, phase: 'defeat', kineticStacks: newKineticStacks });
      return result;
    }

    if (result.enemyDied) {
      const targetId = s.targetInstanceId!;
      const isBoss = isWaveBoss(vault, s.waveIndex, s.totalWaves, s.activeEnemies, targetId);
      const augLootMult = 1 + totalAugmentValue(player.augments, 'loot_chance_pct');
      const augExtraBossRoll = totalAugmentValue(player.augments, 'extra_boss_roll');
      const lootMult = boostMultiplier(player.boosts, 'loot_chance') * augLootMult;
      const flatBonus = getSetBonusValue(player.equipment, 'flat_drop_chance');
      const uptierChance = getSetBonusValue(player.equipment, 'boss_uptier');
      const treasureRolls = (isBoss && hasSkill(player, 'treasure_hunter')
        ? Math.round(activeSkillValue(player, 'treasure_hunter') ?? 2) : 1)
        + (isBoss ? augExtraBossRoll : 0);

      let allResources: DropRollResult['resources'] = [];
      let allEquipment: DropRollResult['equipment'] = [];
      const pityBonus = player.legendaryPity ?? 0;
      // Per-run legendary cap. Track the running remaining inside the loop
      // so multi-roll treasureRolls don't all see "0 dropped yet" and each
      // squeeze in a fresh legendary past the ceiling.
      const runCap = s.isEndless ? LEGENDARY_RUN_CAP_ENDLESS : LEGENDARY_RUN_CAP_VAULT;
      let legendariesThisRun = s.runLegendaryCount;
      for (let r = 0; r < treasureRolls; r++) {
        const remaining = Math.max(0, runCap - legendariesThisRun);
        const drops = rollTierLoot(
          effectiveDropTier(s, vault), lootMult * s.runLootMult * bonusLootMult, isBoss && r === 0,
          s.gamblerCounter + pityBonus, flatBonus, isBoss ? uptierChance : 0, s.levelOvershoot,
          remaining,
        );
        allResources = [...allResources, ...drops.resources];
        allEquipment = [...allEquipment, ...drops.equipment];
        legendariesThisRun += drops.equipment.filter(
          (e) => getItem(e.itemId)?.rarity === 'legendary',
        ).length;
      }
      // Cap to at most one legendary per kill (across all treasure rolls).
      allEquipment = capLegendaryDrops(allEquipment, 1);

      // Whether THIS kill produced an actual legendary item (not just any
      // equipment). Used to reset the soft-pity counter so the per-kill
      // bumpLegendaryPity flow behaves correctly with the diversified pool.
      const droppedLegendary = allEquipment.some(
        (e) => getItem(e.itemId)?.rarity === 'legendary',
      );
      // Final post-cap legendary count for this run — written back to state
      // via baseSet so future kills see the updated ceiling.
      const newRunLegendaryCount = s.runLegendaryCount + (droppedLegendary ? 1 : 0);
      // P2.C Soft-pity: bump here (inside autoTick) so the Farm-button path
      // (instantClear → autoTick) and the manual-combat path (view-layer →
      // autoTick) both reset/increment pity consistently. Previously this
      // only ran in CombatViewStore3D, which meant Farm-mode never reset
      // pity even if it dropped a legendary — pegging it at the 0.30 cap
      // and turning every farm-click into a guaranteed legendary.
      useGameStore.getState().bumpLegendaryPity(droppedLegendary);
      const momentumBonus = hasSkill(player, 'momentum')
        ? Math.round(SPECIAL_MAX * (activeSkillValue(player, 'momentum') ?? 0)) : 0;
      const phantomReset = hasSetBonus(player.equipment, 'execute_reset');

      result.newLoot = allResources;
      result.newEquipmentLoot = allEquipment;
      const totalLoot = [...s.loot, ...allResources];
      const totalEquipmentLoot = [...s.equipmentLoot, ...allEquipment];
      const totalXp = s.earnedXp + Math.round(s.currentEnemy.xpReward * s.scaledHpMult * s.runXpMult * bonusXpMult);
      const cdBaseTick = s.currentEnemy.corruptedDataReward * s.scaledHpMult * s.runXpMult * bonusXpMult;
      const cdBossBonusTick = isBoss ? BOSS_CD_MULTIPLIER : 1;
      const totalCorruptedData = s.earnedCorruptedData + Math.round(cdBaseTick * cdBossBonusTick);

      // P2.B Pyromant Combustion (on_kill explosion): % of player ATK as raw
      // fire damage to every surviving enemy in the room — radius is implicit
      // because dungeons have no spatial model in state. Bypasses DEF (fire =
      // armor-piercing flavor). Chain-kills are downgraded to 1 HP so the
      // next swing finishes them — keeps resolveKill bookkeeping simple.
      const combustionPct = resolvePassiveById(player, 'pyromant', 'combustion', getItem);
      // Combustion stacks ON TOP of Static-Charge — start from the already-
      // pulsed active list so both AOEs can chip the same surviving enemies.
      let postCombustionActive: typeof s.activeEnemies = staticChargedActive;
      if (combustionPct > 0) {
        const combustionDmg = Math.max(1, Math.round(effectiveStats.attack * combustionPct));
        const hits: NonNullable<TickResult['combustionHits']> = [];
        postCombustionActive = staticChargedActive.map((e) => {
          if (e.instanceId === targetId || e.hp <= 0) return e;
          const newHp = Math.max(1, e.hp - combustionDmg);
          hits.push({ instanceId: e.instanceId, damage: combustionDmg, enemyDied: false });
          return { ...e, hp: newHp };
        });
        if (hits.length > 0) result.combustionHits = hits;
      }

      const resolution = resolveKill(postCombustionActive, targetId, vault ?? null, s.waveIndex, s.totalWaves, s.scaledHpMult, s.isEndless);
      const baseSet = {
        kineticStacks: newKineticStacks, fastAttackTicks: newFastTicks,
        specialCharge: Math.min(SPECIAL_MAX, newSpecial + momentumBonus),
        executeCooldown: phantomReset ? 0 : newExecCD,
        gamblerCounter: droppedLegendary ? 0 : s.gamblerCounter + GAMBLER_STEP,
        finalHourActiveUntil: newFinalHourActiveUntil,
        finalHourUsedThisRun: newFinalHourUsedThisRun,
        overchargeStacks: newOverchargeStacks,
        overchargeExpiresAt: newOverchargeExpiresAt,
        staticChargeCounter: newStaticCounter,
        runLegendaryCount: newRunLegendaryCount,
      };

      if (resolution.finished) {
        set({
          enemyHp: 0, activeEnemies: [], targetInstanceId: null, currentEnemy: null,
          loot: totalLoot, equipmentLoot: totalEquipmentLoot,
          earnedXp: totalXp, earnedCorruptedData: totalCorruptedData,
          phase: 'victory', playerHp: newPlayerHp, ...baseSet,
        });
      } else if (resolution.nextTarget) {
        const nt = resolution.nextTarget;
        const ntDef = getEnemy(nt.enemyId);
        result.newEnemy = ntDef ?? null;
        set({
          activeEnemies: resolution.nextActive,
          targetInstanceId: nt.instanceId,
          currentEnemy: ntDef ?? null,
          enemyHp: nt.hp, enemyMaxHp: nt.maxHp,
          waveIndex: resolution.nextWaveIndex,
          loot: totalLoot, equipmentLoot: totalEquipmentLoot,
          earnedXp: totalXp, earnedCorruptedData: totalCorruptedData,
          playerHp: newPlayerHp, ...baseSet,
        });
      } else {
        set({
          activeEnemies: [], targetInstanceId: null, currentEnemy: null,
          enemyHp: 0, enemyMaxHp: 0,
          loot: totalLoot, equipmentLoot: totalEquipmentLoot,
          earnedXp: totalXp, earnedCorruptedData: totalCorruptedData,
          playerHp: newPlayerHp, ...baseSet,
        });
      }
      return result;
    }

    // Legendary: Chain Lightning (legendary_lightning) — 30% proc when the target
    // survived. Strikes a random other live enemy for 60% damage. To keep multi-kill
    // resolution simple, lethal chain hits are downgraded to leave 1 HP — the next
    // swing or chain finishes them off.
    let chainedActive: typeof s.activeEnemies | null = null;
    if (
      finalEnemyHp > 0
      && hasLegendaryEffect(player.equipment, 'chain_lightning')
      && Math.random() < 0.30
    ) {
      const candidates = s.activeEnemies.filter(
        (e) => e.instanceId !== s.targetInstanceId && e.hp > 0,
      );
      if (candidates.length > 0) {
        const tgt = candidates[Math.floor(Math.random() * candidates.length)];
        const chainDmg = Math.max(1, Math.round(pDmg * 0.6));
        const newHp = Math.max(1, tgt.hp - chainDmg);
        chainedActive = s.activeEnemies.map((e) =>
          e.instanceId === tgt.instanceId ? { ...e, hp: newHp } : e,
        );
        result.chainLightning = { instanceId: tgt.instanceId, damage: chainDmg, enemyDied: false };
      }
    }

    // Apply non-fatal damage to the active enemy entry. Static-Charge AOE
    // changes already baked in via `staticChargedActive`.
    const updatedActive = (chainedActive ?? staticChargedActive).map((e) =>
      e.instanceId === s.targetInstanceId ? { ...e, hp: finalEnemyHp } : e,
    );
    set({
      playerHp: newPlayerHp, enemyHp: finalEnemyHp,
      activeEnemies: updatedActive,
      specialCharge: newSpecial,
      kineticStacks: newKineticStacks, fastAttackTicks: newFastTicks,
      executeCooldown: newExecCD,
      finalHourActiveUntil: newFinalHourActiveUntil,
      finalHourUsedThisRun: newFinalHourUsedThisRun,
      overchargeStacks: newOverchargeStacks,
      overchargeExpiresAt: newOverchargeExpiresAt,
      staticChargeCounter: newStaticCounter,
    });
    return result;
  },

  triggerSpecial: (playerStats, player) => {
    const s = get();
    if (s.phase !== 'combat' || !s.currentEnemy || !s.vaultId) return null;
    if (s.specialCharge < SPECIAL_MAX) return null;
    const vault = getVault(s.vaultId);
    if (!vault) return null;

    // Bulwark Frost-Nova grants +30% DEF for 5 sec after the cast. Applied
    // as a multiplier on the already-summed (level + kinetic + equipment)
    // defense so it scales with the player's actual mitigation pool.
    const novaDefMult = s.frostNovaDefUntil > Date.now() ? 1.30 : 1.0;
    const effectiveStats: PlayerCombatStats = {
      ...playerStats,
      defense: Math.round((playerStats.defense + s.kineticStacks) * novaDefMult),
    };
    const useShieldBash = hasSkill(player, 'shield_bash');
    return executeSpecialAttack(s, effectiveStats, player, vault, useShieldBash, set);
  },

  instantClear: (playerStats, player) => {
    const MAX_ITER = 2000;
    for (let i = 0; i < MAX_ITER; i++) {
      const s = get();
      if (s.phase !== 'combat') break;
      // After clearing a wave, the autoTick leaves activeEnemies empty and
      // currentEnemy null — the 3D layer normally calls advanceToNextRoom()
      // when the player walks through the door. In farm mode there's no
      // visual layer, so we trigger the next wave manually.
      if (s.activeEnemies.length === 0 || !s.currentEnemy) {
        get().advanceToNextRoom();
        // If still empty after advance → no more waves → mark victory.
        if (get().activeEnemies.length === 0) {
          set({ phase: 'victory' });
          break;
        }
        continue;
      }
      get().autoTick(playerStats, player);
    }
  },

  claimLoot: () => {
    const s = get();
    const vault = s.vaultId ? getVault(s.vaultId) : null;
    const groupBonus = groupLootBonus(useGroupStore.getState().currentSession);
    const out = {
      loot: s.loot,
      equipment: s.equipmentLoot,
      xp: Math.round(s.earnedXp * groupBonus),
      corruptedData: Math.round(s.earnedCorruptedData * groupBonus),
      vaultTier: vault?.tier ?? 0,
      groupBonus,
    };
    emitRunTelemetry('victory', s);
    // Run-streak: only ACTIVE engagement counts. Farm (instant-resolve
    // click) is excluded — the streak is meant to reward the player who
    // sits through combat, not the one who one-shots the loot button.
    // Manual / Endless / Boss runs all qualify (player has actually
    // engaged with combat in those modes).
    if (s.runMode !== 'farm') {
      const player = useGameStore.getState().player;
      if (player) {
        useGameStore.setState({ player: pushStreakEntry(player) });
      }
    }
    set({ ...IDLE_STATE, ...PER_RUN_RESET, phase: 'idle', activeEnemies: [], targetInstanceId: null });
    return out;
  },

  reset: () => set({ ...IDLE_STATE, ...PER_RUN_RESET, activeEnemies: [], targetInstanceId: null }),
}));
